import React, { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { reportsService, addonsService } from '../services/api';
import API_CONFIG from '../config/api';
import DynamicReportsTable from './DynamicReportsTable';
import { useIsMobile } from '../hooks';
import { Hourglass as HourglassIcon } from 'lucide-react';

/**
 * NewReports Component
 * 
 * Comprehensive reporting interface with multiple report types:
 * - Master Booking Report
 * - Occupancy Report
 * - Course Participants Report
 * - Accounts Report (with GST breakdown)
 * - Check-in Report
 * - Check-out Report
 * - OMG Experience Report
 * - Daily Summary Report
 */

/**
 * Flatten nested JSON arrays into separate columns
 * Example: rooms array with 2 items becomes roomNumber_1, roomTypeName_1, roomNumber_2, roomTypeName_2
 * Also flattens nested priceBreakdowns within each item
 */
const flattenNestedArrays = (data, arrayKeys = ['rooms', 'beds', 'courses', 'addOns', 'payments', 'refunds']) => {
  if (!data || data.length === 0) return { flattenedData: data, additionalColumns: [] };

  // Fields to exclude from flattening (not needed in reports)
  const excludedFields = new Set([
    'roomTypeDesc', 'roomGST', 'roomDiscount', 'roomFees', 'roomId',
    'bedGST', 'bedDiscount', 'bedFees', 'bedId',
    'courseId', 'courseDesc', 'courseGST', 'courseDiscount', 'courseFees',
    'addonId', 'addonServiceId', 'addonGST', 'addonDiscount', 'addonFees',
    'paymentGST',
    'propertyAddress' // Usually verbose and not needed in reports
  ]);
  
  // Fields to exclude from priceBreakdown flattening
  const excludedPriceBreakdownFields = new Set([
    'breakdownType',
    'calculationType', 
    'value'
  ]);

  // Step 1: Find the maximum count for each array key across all records
  // Also find max priceBreakdowns count for each entity type
  const maxCounts = {};
  const maxPriceBreakdownCounts = {};
  
  arrayKeys.forEach(key => {
    maxCounts[key] = 0;
    maxPriceBreakdownCounts[key] = 0;
  });

  data.forEach(row => {
    arrayKeys.forEach(key => {
      if (row[key]) {
        try {
          const parsed = typeof row[key] === 'string' ? JSON.parse(row[key]) : row[key];
          if (Array.isArray(parsed)) {
            maxCounts[key] = Math.max(maxCounts[key], parsed.length);
            
            // Find max priceBreakdowns within this entity type
            parsed.forEach(item => {
              if (item.priceBreakdowns && Array.isArray(item.priceBreakdowns)) {
                maxPriceBreakdownCounts[key] = Math.max(
                  maxPriceBreakdownCounts[key], 
                  item.priceBreakdowns.length
                );
              }
            });
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
    });
  });

  // Step 2: Determine all unique keys from all items in arrays (excluding priceBreakdowns and excluded fields)
  // And determine priceBreakdown schema
  const arrayKeyStructure = {};
  const priceBreakdownKeys = new Set();
  
  arrayKeys.forEach(arrayKey => {
    const allKeys = new Set();
    data.forEach(row => {
      if (row[arrayKey]) {
        try {
          const parsed = typeof row[arrayKey] === 'string' ? JSON.parse(row[arrayKey]) : row[arrayKey];
          if (Array.isArray(parsed)) {
            parsed.forEach(item => {
              if (typeof item === 'object' && item !== null) {
                Object.keys(item).forEach(k => {
                  // Exclude priceBreakdowns and fields in exclusion list
                  if (k !== 'priceBreakdowns' && !excludedFields.has(k)) {
                    allKeys.add(k);
                  }
                });
                
                // Collect priceBreakdown keys (excluding unwanted fields)
                if (item.priceBreakdowns && Array.isArray(item.priceBreakdowns)) {
                  item.priceBreakdowns.forEach(breakdown => {
                    if (typeof breakdown === 'object' && breakdown !== null) {
                      Object.keys(breakdown).forEach(bk => {
                        if (!excludedPriceBreakdownFields.has(bk)) {
                          priceBreakdownKeys.add(bk);
                        }
                      });
                    }
                  });
                }
              }
            });
          }
        } catch (e) {
          // Ignore
        }
      }
    });
    arrayKeyStructure[arrayKey] = Array.from(allKeys);
  });
  
  const priceBreakdownSchema = Array.from(priceBreakdownKeys);

  // Step 3: Generate dynamic columns
  const additionalColumns = [];
  arrayKeys.forEach(arrayKey => {
    const count = maxCounts[arrayKey];
    const itemKeys = arrayKeyStructure[arrayKey];
    const pbCount = maxPriceBreakdownCounts[arrayKey];
    
    for (let i = 1; i <= count; i++) {
      // Add columns for main entity fields (excluding priceBreakdowns)
      itemKeys.forEach(itemKey => {
        const columnKey = `${arrayKey}_${i}_${itemKey}`;
        const label = `${arrayKey.charAt(0).toUpperCase() + arrayKey.slice(1)} ${i} - ${itemKey}`;
        
        // Determine column type based on key name
        let columnConfig = { key: columnKey, label, minWidth: '150px' };
        
        if (itemKey.toLowerCase().includes('amount') || 
            itemKey.toLowerCase().includes('price') || 
            itemKey.toLowerCase().includes('gst') ||
            itemKey.toLowerCase().includes('fee') ||
            itemKey.toLowerCase().includes('discount') ||
            itemKey.toLowerCase().includes('paid') ||
            itemKey.toLowerCase().includes('refund')) {
          columnConfig.type = 'currency';
          columnConfig.align = 'right';
        } else if (itemKey.toLowerCase().includes('date') || 
                   itemKey.toLowerCase().includes('createdat') ||
                   itemKey.toLowerCase().includes('updatedat')) {
          columnConfig.type = 'datetime';
        } else if (itemKey.toLowerCase().includes('count') || 
                   itemKey.toLowerCase().includes('quantity') ||
                   itemKey.toLowerCase().includes('nights') ||
                   itemKey.toLowerCase().includes('duration')) {
          columnConfig.align = 'center';
        }
        
        additionalColumns.push(columnConfig);
      });
      
      // Add columns for priceBreakdowns nested within this entity item
      for (let pb = 1; pb <= pbCount; pb++) {
        priceBreakdownSchema.forEach(pbKey => {
          const columnKey = `${arrayKey}_${i}_priceBreakdown_${pb}_${pbKey}`;
          const label = `${arrayKey.charAt(0).toUpperCase() + arrayKey.slice(1)} ${i} - PB ${pb} - ${pbKey}`;
          
          let columnConfig = { key: columnKey, label, minWidth: '150px' };
          
          if (pbKey.toLowerCase().includes('amount') || 
              pbKey.toLowerCase().includes('value')) {
            columnConfig.type = 'currency';
            columnConfig.align = 'right';
          }
          
          additionalColumns.push(columnConfig);
        });
      }
    }
  });

  // Step 4: Flatten the data
  const flattenedData = data.map(row => {
    const flatRow = { ...row };
    
    arrayKeys.forEach(arrayKey => {
      const itemKeys = arrayKeyStructure[arrayKey];
      const pbMaxCount = maxPriceBreakdownCounts[arrayKey];
      
      if (row[arrayKey]) {
        try {
          const parsed = typeof row[arrayKey] === 'string' ? JSON.parse(row[arrayKey]) : row[arrayKey];
          if (Array.isArray(parsed)) {
            // Add data for each item
            parsed.forEach((item, index) => {
              const itemNumber = index + 1;
              
              // Add main entity fields
              itemKeys.forEach(itemKey => {
                const columnKey = `${arrayKey}_${itemNumber}_${itemKey}`;
                flatRow[columnKey] = item[itemKey] !== undefined ? item[itemKey] : null;
              });
              
              // Add priceBreakdowns for this item
              if (item.priceBreakdowns && Array.isArray(item.priceBreakdowns)) {
                item.priceBreakdowns.forEach((breakdown, pbIndex) => {
                  const pbNumber = pbIndex + 1;
                  priceBreakdownSchema.forEach(pbKey => {
                    const columnKey = `${arrayKey}_${itemNumber}_priceBreakdown_${pbNumber}_${pbKey}`;
                    flatRow[columnKey] = breakdown[pbKey] !== undefined ? breakdown[pbKey] : null;
                  });
                });
                
                // Fill remaining priceBreakdown slots with null
                for (let pb = item.priceBreakdowns.length + 1; pb <= pbMaxCount; pb++) {
                  priceBreakdownSchema.forEach(pbKey => {
                    const columnKey = `${arrayKey}_${itemNumber}_priceBreakdown_${pb}_${pbKey}`;
                    flatRow[columnKey] = null;
                  });
                }
              } else {
                // No priceBreakdowns for this item, fill all with null
                for (let pb = 1; pb <= pbMaxCount; pb++) {
                  priceBreakdownSchema.forEach(pbKey => {
                    const columnKey = `${arrayKey}_${itemNumber}_priceBreakdown_${pb}_${pbKey}`;
                    flatRow[columnKey] = null;
                  });
                }
              }
            });
            
            // Fill remaining entity slots with null
            for (let i = parsed.length + 1; i <= maxCounts[arrayKey]; i++) {
              itemKeys.forEach(itemKey => {
                const columnKey = `${arrayKey}_${i}_${itemKey}`;
                flatRow[columnKey] = null;
              });
              
              // Fill priceBreakdowns for missing entities
              for (let pb = 1; pb <= pbMaxCount; pb++) {
                priceBreakdownSchema.forEach(pbKey => {
                  const columnKey = `${arrayKey}_${i}_priceBreakdown_${pb}_${pbKey}`;
                  flatRow[columnKey] = null;
                });
              }
            }
          }
        } catch (e) {
          // If parse fails, fill all with null
          for (let i = 1; i <= maxCounts[arrayKey]; i++) {
            itemKeys.forEach(itemKey => {
              const columnKey = `${arrayKey}_${i}_${itemKey}`;
              flatRow[columnKey] = null;
            });
            
            for (let pb = 1; pb <= pbMaxCount; pb++) {
              priceBreakdownSchema.forEach(pbKey => {
                const columnKey = `${arrayKey}_${i}_priceBreakdown_${pb}_${pbKey}`;
                flatRow[columnKey] = null;
              });
            }
          }
        }
      } else {
        // If key doesn't exist, fill with null
        for (let i = 1; i <= maxCounts[arrayKey]; i++) {
          itemKeys.forEach(itemKey => {
            const columnKey = `${arrayKey}_${i}_${itemKey}`;
            flatRow[columnKey] = null;
          });
          
          for (let pb = 1; pb <= pbMaxCount; pb++) {
            priceBreakdownSchema.forEach(pbKey => {
              const columnKey = `${arrayKey}_${i}_priceBreakdown_${pb}_${pbKey}`;
              flatRow[columnKey] = null;
            });
          }
        }
      }
      
      // Remove original array column
      delete flatRow[arrayKey];
    });
    
    return flatRow;
  });

  return { flattenedData, additionalColumns };
};

/**
 * Merge nested JSON arrays into comma-separated values in single columns
 * Example: rooms array with 2 items becomes single roomNumber column with "101, 102"
 */
const mergeNestedArrays = (data, arrayKeys = ['rooms', 'beds', 'courses', 'addOns', 'payments', 'refunds']) => {
  if (!data || data.length === 0) return { mergedData: data, additionalColumns: [] };

  // Fields to exclude from merging (same as flatten)
  const excludedFields = new Set([
    'roomTypeDesc', 'roomGST', 'roomDiscount', 'roomFees', 'roomId',
    'bedGST', 'bedDiscount', 'bedFees', 'bedId',
    'courseId', 'courseDesc', 'courseGST', 'courseDiscount', 'courseFees',
    'addonId', 'addonServiceId', 'addonGST', 'addonDiscount', 'addonFees',
    'paymentGST',
    'propertyAddress'
  ]);
  
  const excludedPriceBreakdownFields = new Set([
    'breakdownType',
    'calculationType', 
    'value'
  ]);

  // Step 1: Determine all unique keys from all items in arrays
  const arrayKeyStructure = {};
  const priceBreakdownKeys = new Set();
  
  arrayKeys.forEach(arrayKey => {
    const allKeys = new Set();
    data.forEach(row => {
      if (row[arrayKey]) {
        try {
          const parsed = typeof row[arrayKey] === 'string' ? JSON.parse(row[arrayKey]) : row[arrayKey];
          if (Array.isArray(parsed)) {
            parsed.forEach(item => {
              if (typeof item === 'object' && item !== null) {
                Object.keys(item).forEach(k => {
                  if (k !== 'priceBreakdowns' && !excludedFields.has(k)) {
                    allKeys.add(k);
                  }
                });
                
                if (item.priceBreakdowns && Array.isArray(item.priceBreakdowns)) {
                  item.priceBreakdowns.forEach(breakdown => {
                    if (typeof breakdown === 'object' && breakdown !== null) {
                      Object.keys(breakdown).forEach(bk => {
                        if (!excludedPriceBreakdownFields.has(bk)) {
                          priceBreakdownKeys.add(bk);
                        }
                      });
                    }
                  });
                }
              }
            });
          }
        } catch (e) {
          // Ignore
        }
      }
    });
    arrayKeyStructure[arrayKey] = Array.from(allKeys);
  });
  
  const priceBreakdownSchema = Array.from(priceBreakdownKeys);

  // Helper function to check if field is numeric/currency
  const isNumericField = (fieldName) => {
    const lowerField = fieldName.toLowerCase();
    return lowerField.includes('amount') || 
           lowerField.includes('price') || 
           lowerField.includes('gst') ||
           lowerField.includes('fee') ||
           lowerField.includes('discount') ||
           lowerField.includes('paid') ||
           lowerField.includes('refund') ||
           lowerField.includes('total') ||
           lowerField.includes('revenue') ||
           lowerField.includes('cost');
  };

  // Step 2: Generate merged columns
  const additionalColumns = [];
  arrayKeys.forEach(arrayKey => {
    const itemKeys = arrayKeyStructure[arrayKey];
    
    // Add columns for main entity fields
    itemKeys.forEach(itemKey => {
      const isNumeric = isNumericField(itemKey);
      const isDate = itemKey.toLowerCase().includes('date') || 
                     itemKey.toLowerCase().includes('createdat') ||
                     itemKey.toLowerCase().includes('updatedat');
      
      if (isNumeric) {
        // For numeric fields, create two columns: individual values and total
        
        // Column 1: Individual values (comma-separated)
        const columnKey = `${arrayKey}_${itemKey}`;
        const label = `${arrayKey.charAt(0).toUpperCase() + arrayKey.slice(1)} - ${itemKey} (Items)`;
        additionalColumns.push({ 
          key: columnKey, 
          label, 
          minWidth: '200px',
          align: 'right'
        });
        
        // Column 2: Total
        const totalColumnKey = `${arrayKey}_${itemKey}_total`;
        const totalLabel = `${arrayKey.charAt(0).toUpperCase() + arrayKey.slice(1)} - ${itemKey} (Total)`;
        additionalColumns.push({ 
          key: totalColumnKey, 
          label: totalLabel, 
          minWidth: '120px',
          type: 'currency',
          align: 'right'
        });
      } else {
        // For non-numeric fields, just show comma-separated values
        const columnKey = `${arrayKey}_${itemKey}`;
        const label = `${arrayKey.charAt(0).toUpperCase() + arrayKey.slice(1)} - ${itemKey}`;
        
        let columnConfig = { key: columnKey, label, minWidth: '200px' };
        
        if (isDate) {
          columnConfig.type = 'datetime';
        }
        
        additionalColumns.push(columnConfig);
      }
    });
    
    // Add columns for priceBreakdowns
    priceBreakdownSchema.forEach(pbKey => {
      const isNumeric = isNumericField(pbKey);
      
      if (isNumeric) {
        // Column 1: Individual values (comma-separated)
        const columnKey = `${arrayKey}_priceBreakdown_${pbKey}`;
        const label = `${arrayKey.charAt(0).toUpperCase() + arrayKey.slice(1)} - PB - ${pbKey} (Items)`;
        additionalColumns.push({ 
          key: columnKey, 
          label, 
          minWidth: '200px',
          align: 'right'
        });
        
        // Column 2: Total
        const totalColumnKey = `${arrayKey}_priceBreakdown_${pbKey}_total`;
        const totalLabel = `${arrayKey.charAt(0).toUpperCase() + arrayKey.slice(1)} - PB - ${pbKey} (Total)`;
        additionalColumns.push({ 
          key: totalColumnKey, 
          label: totalLabel, 
          minWidth: '120px',
          type: 'currency',
          align: 'right'
        });
      } else {
        const columnKey = `${arrayKey}_priceBreakdown_${pbKey}`;
        const label = `${arrayKey.charAt(0).toUpperCase() + arrayKey.slice(1)} - PB - ${pbKey}`;
        additionalColumns.push({ 
          key: columnKey, 
          label, 
          minWidth: '200px'
        });
      }
    });
  });

  // Step 3: Merge the data
  const mergedData = data.map(row => {
    const mergedRow = { ...row };
    
    arrayKeys.forEach(arrayKey => {
      const itemKeys = arrayKeyStructure[arrayKey];
      
      if (row[arrayKey]) {
        try {
          const parsed = typeof row[arrayKey] === 'string' ? JSON.parse(row[arrayKey]) : row[arrayKey];
          if (Array.isArray(parsed)) {
            // Collect values for each field across all items
            const fieldValues = {};
            itemKeys.forEach(itemKey => {
              fieldValues[itemKey] = [];
            });
            
            const priceBreakdownValues = {};
            priceBreakdownSchema.forEach(pbKey => {
              priceBreakdownValues[pbKey] = [];
            });
            
            parsed.forEach(item => {
              // Collect main entity field values
              itemKeys.forEach(itemKey => {
                const value = item[itemKey];
                if (value !== undefined && value !== null) {
                  fieldValues[itemKey].push(value);
                }
              });
              
              // Collect priceBreakdown values
              if (item.priceBreakdowns && Array.isArray(item.priceBreakdowns)) {
                item.priceBreakdowns.forEach(breakdown => {
                  priceBreakdownSchema.forEach(pbKey => {
                    const value = breakdown[pbKey];
                    if (value !== undefined && value !== null) {
                      priceBreakdownValues[pbKey].push(value);
                    }
                  });
                });
              }
            });
            
            // Set merged values (comma-separated) and totals for numeric fields
            itemKeys.forEach(itemKey => {
              const columnKey = `${arrayKey}_${itemKey}`;
              const values = fieldValues[itemKey];
              
              if (values.length > 0) {
                if (isNumericField(itemKey)) {
                  // For numeric fields, set both comma-separated and total
                  mergedRow[columnKey] = values.join(', ');
                  
                  // Calculate total (convert to numbers and sum)
                  const total = values.reduce((sum, val) => {
                    const num = parseFloat(val);
                    return sum + (isNaN(num) ? 0 : num);
                  }, 0);
                  
                  mergedRow[`${columnKey}_total`] = total;
                } else {
                  // For non-numeric fields, just comma-separated
                  mergedRow[columnKey] = values.join(', ');
                }
              } else {
                mergedRow[columnKey] = null;
                if (isNumericField(itemKey)) {
                  mergedRow[`${columnKey}_total`] = null;
                }
              }
            });
            
            priceBreakdownSchema.forEach(pbKey => {
              const columnKey = `${arrayKey}_priceBreakdown_${pbKey}`;
              const values = priceBreakdownValues[pbKey];
              
              if (values.length > 0) {
                if (isNumericField(pbKey)) {
                  // For numeric fields, set both comma-separated and total
                  mergedRow[columnKey] = values.join(', ');
                  
                  // Calculate total
                  const total = values.reduce((sum, val) => {
                    const num = parseFloat(val);
                    return sum + (isNaN(num) ? 0 : num);
                  }, 0);
                  
                  mergedRow[`${columnKey}_total`] = total;
                } else {
                  // For non-numeric fields, just comma-separated
                  mergedRow[columnKey] = values.join(', ');
                }
              } else {
                mergedRow[columnKey] = null;
                if (isNumericField(pbKey)) {
                  mergedRow[`${columnKey}_total`] = null;
                }
              }
            });
          }
        } catch (e) {
          // If parse fails, set all to null
          itemKeys.forEach(itemKey => {
            const columnKey = `${arrayKey}_${itemKey}`;
            mergedRow[columnKey] = null;
            if (isNumericField(itemKey)) {
              mergedRow[`${columnKey}_total`] = null;
            }
          });
          
          priceBreakdownSchema.forEach(pbKey => {
            const columnKey = `${arrayKey}_priceBreakdown_${pbKey}`;
            mergedRow[columnKey] = null;
            if (isNumericField(pbKey)) {
              mergedRow[`${columnKey}_total`] = null;
            }
          });
        }
      } else {
        // If key doesn't exist, set to null
        itemKeys.forEach(itemKey => {
          const columnKey = `${arrayKey}_${itemKey}`;
          mergedRow[columnKey] = null;
          if (isNumericField(itemKey)) {
            mergedRow[`${columnKey}_total`] = null;
          }
        });
        
        priceBreakdownSchema.forEach(pbKey => {
          const columnKey = `${arrayKey}_priceBreakdown_${pbKey}`;
          mergedRow[columnKey] = null;
          if (isNumericField(pbKey)) {
            mergedRow[`${columnKey}_total`] = null;
          }
        });
      }
      
      // Remove original array column
      delete mergedRow[arrayKey];
    });
    
    return mergedRow;
  });

  return { mergedData, additionalColumns };
};

/**
 * Create Accounts View - focused on financial/tax data
 * Combines rooms + beds (same price breakdown format) into merged columns,
 * shows addons in expanded view, and individual payments/refunds.
 * Removes IDs, status columns, and non-numeric text fields.
 */
// Featured addon services that get their own dedicated columns in the accounts view.
// All other addons are grouped under "Remaining Addon Services".
const FEATURED_ADDON_SERVICES = [
  { key: 'robes', label: 'Robes' },
  { key: 'cafe',  label: 'Cafe'  },
];

// Resolve the service name from an addon item (handles different backend field name conventions)
const getAddonServiceName = (addon) =>
  (addon.addonServiceName || addon.serviceName || addon.name || addon.addonName || addon.service || '').toLowerCase().trim();

const createAccountsView = (data, addonServices = []) => {
  if (!data || data.length === 0) return { accountsData: data, additionalColumns: [] };

  // Helper: parse JSON array from string or return as-is
  const parseArr = (val) => {
    if (!val) return [];
    try {
      const parsed = typeof val === 'string' ? JSON.parse(val) : val;
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) { return []; }
  };

  // Helper: get PB amount by lowercase name from a priceBreakdowns/breakdownItems array
  const getPB = (pbs, name) => {
    if (!Array.isArray(pbs)) return 0;
    const found = pbs.find(pb => (pb.name || '').toLowerCase().trim() === name);
    return found ? (parseFloat(found.amount) || 0) : 0;
  };

  // Helper: sum all TAX-type PBs from an array
  const sumTaxPBs = (pbs) => {
    if (!Array.isArray(pbs)) return 0;
    return pbs.filter(pb => (pb.breakdownType || '').toUpperCase() === 'TAX')
      .reduce((sum, pb) => sum + (parseFloat(pb.amount) || 0), 0);
  };

  // Step 1: Determine max payment/refund counts
  let maxPaymentsCount = 0;
  let maxRefundsCount = 0;

  data.forEach(row => {
    maxPaymentsCount = Math.max(maxPaymentsCount, parseArr(row.payments).length);
    maxRefundsCount = Math.max(maxRefundsCount, parseArr(row.refunds).length);
  });

  // Step 2: Generate columns
  const additionalColumns = [];

  // Rooms + Beds info
  additionalColumns.push({ key: 'acc_item_names', label: 'Items (Rooms+Beds)', minWidth: '250px' });
  additionalColumns.push({ key: 'acc_item_nights', label: 'Nights', minWidth: '150px', align: 'right' });

  // Computed financial columns
  additionalColumns.push({ key: 'acc_total_without_tax', label: 'Total Without Tax', minWidth: '140px', type: 'currency', align: 'right' });
  additionalColumns.push({ key: 'acc_room_base_per_day', label: 'Room Base Per Day', minWidth: '140px', type: 'currency', align: 'right' });
  additionalColumns.push({ key: 'acc_room_base', label: 'Room Base', minWidth: '120px', type: 'currency', align: 'right' });
  additionalColumns.push({ key: 'acc_food_base', label: 'Food Base', minWidth: '120px', type: 'currency', align: 'right' });
  additionalColumns.push({ key: 'acc_course_name', label: 'Course', minWidth: '180px' });
  additionalColumns.push({ key: 'acc_course_base', label: 'Course Base', minWidth: '120px', type: 'currency', align: 'right' });
  // Featured addon service columns (one set per featured service)
  FEATURED_ADDON_SERVICES.forEach(({ key, label }) => {
    additionalColumns.push({ key: `acc_${key}_base`, label: `${label} Base`, minWidth: '120px', type: 'currency', align: 'right' });
  });
  // Remaining (non-featured) addon services
  additionalColumns.push({ key: 'acc_remaining_addons_base', label: 'Remaining Addon Services Base', minWidth: '190px', type: 'currency', align: 'right' });

  additionalColumns.push({ key: 'acc_total_base', label: 'Total Base', minWidth: '120px', type: 'currency', align: 'right' });

  // GST columns
  additionalColumns.push({ key: 'acc_room_sgst', label: 'Room SGST', minWidth: '110px', type: 'currency', align: 'right' });
  additionalColumns.push({ key: 'acc_room_cgst', label: 'Room CGST', minWidth: '110px', type: 'currency', align: 'right' });
  additionalColumns.push({ key: 'acc_food_sgst', label: 'Food SGST', minWidth: '110px', type: 'currency', align: 'right' });
  additionalColumns.push({ key: 'acc_food_cgst', label: 'Food CGST', minWidth: '110px', type: 'currency', align: 'right' });
  additionalColumns.push({ key: 'acc_course_sgst', label: 'Course SGST', minWidth: '120px', type: 'currency', align: 'right' });
  additionalColumns.push({ key: 'acc_course_cgst', label: 'Course CGST', minWidth: '120px', type: 'currency', align: 'right' });
  // Featured addon GST columns
  FEATURED_ADDON_SERVICES.forEach(({ key, label }) => {
    additionalColumns.push({ key: `acc_${key}_sgst`, label: `${label} SGST`, minWidth: '110px', type: 'currency', align: 'right' });
    additionalColumns.push({ key: `acc_${key}_cgst`, label: `${label} CGST`, minWidth: '110px', type: 'currency', align: 'right' });
  });
  additionalColumns.push({ key: 'acc_remaining_addons_sgst', label: 'Remaining Addon Services SGST', minWidth: '200px', type: 'currency', align: 'right' });
  additionalColumns.push({ key: 'acc_remaining_addons_cgst', label: 'Remaining Addon Services CGST', minWidth: '200px', type: 'currency', align: 'right' });

  // Totals / payments / refunds
  additionalColumns.push({ key: 'acc_balance', label: 'Balance Due', minWidth: '120px', type: 'currency', align: 'right' });
  additionalColumns.push({ key: 'acc_payment_total', label: 'Payment Total', minWidth: '120px', type: 'currency', align: 'right' });
  additionalColumns.push({ key: 'acc_cash_total', label: 'Cash Payment Total', minWidth: '140px', type: 'currency', align: 'right' });

  for (let p = 1; p <= maxPaymentsCount; p++) {
    additionalColumns.push({ key: `acc_payment_${p}_amount`, label: `Payment ${p} Amt`, minWidth: '130px', type: 'currency', align: 'right' });
    additionalColumns.push({ key: `acc_payment_${p}_date`, label: `Payment ${p} Date`, minWidth: '120px', type: 'datetime' });
    additionalColumns.push({ key: `acc_payment_${p}_method`, label: `Payment ${p} Method`, minWidth: '120px' });
  }

  for (let r = 1; r <= maxRefundsCount; r++) {
    additionalColumns.push({ key: `acc_refund_${r}_amount`, label: `Refund ${r} Amt`, minWidth: '130px', type: 'currency', align: 'right' });
    additionalColumns.push({ key: `acc_refund_${r}_date`, label: `Refund ${r} Date`, minWidth: '120px', type: 'datetime' });
    additionalColumns.push({ key: `acc_refund_${r}_method`, label: `Refund ${r} Method`, minWidth: '120px' });
  }

  // Step 3: Process each row
  const accountsData = data.map(row => {
    const accRow = { ...row };
    delete accRow.rooms;
    delete accRow.beds;
    delete accRow.courses;
    delete accRow.addOns;
    delete accRow.payments;
    delete accRow.refunds;

    // --- Parse all arrays ---
    const rooms = parseArr(row.rooms);
    const beds = parseArr(row.beds);
    const allRoomsBeds = [...rooms, ...beds];
    const courses = parseArr(row.courses);
    const addons = parseArr(row.addOns);
    const payments = parseArr(row.payments);
    const refunds = parseArr(row.refunds);

    // --- Rooms+Beds info ---
    const itemNames = allRoomsBeds.map((item, i) => {
      if (rooms.includes(item)) {
        return item.roomNumber || item.roomTypeName || 'Room';
      } else {
        const parts = [];
        if (item.roomTypeName) parts.push(item.roomTypeName);
        if (item.roomNumber) parts.push(item.roomNumber);
        parts.push(`Bed ${item.bedNumber || ''}`);
        return parts.join(' - ').trim();
      }
    });
    accRow.acc_item_names = itemNames.join(', ') || null;
    accRow.acc_item_nights = allRoomsBeds.map(i => i.nights || 0).join(', ') || null;

    // --- Compute room/bed totals from PBs (lowercase name match, × nights) ---
    let roomBase = 0, roomBasePerDay = 0, foodBase = 0;
    let roomSgst = 0, roomCgst = 0, foodSgst = 0, foodCgst = 0;
    let totalTax = 0;

    allRoomsBeds.forEach(item => {
      const pbs = item.priceBreakdowns || item.breakdownItems || [];
      const nights = item.nights || 1;
      roomBasePerDay += getPB(pbs, 'base');
      roomBase += getPB(pbs, 'base') * nights;
      foodBase += getPB(pbs, 'food') * nights;
      roomSgst += getPB(pbs, 'room sgst') * nights;
      roomCgst += getPB(pbs, 'room cgst') * nights;
      foodSgst += getPB(pbs, 'food sgst') * nights;
      foodCgst += getPB(pbs, 'food cgst') * nights;
      totalTax += sumTaxPBs(pbs) * nights;
    });

    accRow.acc_room_base_per_day = roomBasePerDay || null;
    accRow.acc_room_base = roomBase || null;
    accRow.acc_food_base = foodBase || null;
    accRow.acc_room_sgst = roomSgst || null;
    accRow.acc_room_cgst = roomCgst || null;
    accRow.acc_food_sgst = foodSgst || null;
    accRow.acc_food_cgst = foodCgst || null;

    // --- Courses (sum all courses) ---
    let courseBase = 0, courseSgst = 0, courseCgst = 0;
    const courseNames = [];
    courses.forEach(course => {
      const pbs = course.priceBreakdowns || course.breakdownItems || [];
      const qty = parseInt(course.quantity) || 1;
      courseNames.push(course.courseName || course.name || '-');
      courseBase += getPB(pbs, 'base') * qty;
      courseSgst += getPB(pbs, 'sgst') * qty;
      courseCgst += getPB(pbs, 'cgst') * qty;
      totalTax += sumTaxPBs(pbs) * qty;
    });

    accRow.acc_course_name = courseNames.join(', ') || null;
    accRow.acc_course_base = courseBase || null;
    accRow.acc_course_sgst = courseSgst || null;
    accRow.acc_course_cgst = courseCgst || null;

    // --- Addons: featured services get dedicated columns; the rest go to "Remaining" ---
    const featuredTotals = {};
    FEATURED_ADDON_SERVICES.forEach(({ key }) => {
      featuredTotals[key] = { base: 0, sgst: 0, cgst: 0 };
    });
    let remainingAddonsBase = 0, remainingAddonsSgst = 0, remainingAddonsCgst = 0;

    addons.forEach(addon => {
      const pbs = addon.priceBreakdowns || addon.breakdownItems || [];
      const qty = parseInt(addon.quantity) || 1;
      const svcName = getAddonServiceName(addon);
      const featured = FEATURED_ADDON_SERVICES.find(f => svcName === f.key || svcName.includes(f.key));

      const baseFromPB = getPB(pbs, 'base');
      const base = baseFromPB > 0
        ? baseFromPB * qty
        : (parseFloat(addon.price) || parseFloat(addon.finalAmount) || 0);
      const sgst = getPB(pbs, 'sgst') * qty;
      const cgst = getPB(pbs, 'cgst') * qty;
      totalTax += sumTaxPBs(pbs) * qty;

      if (featured) {
        featuredTotals[featured.key].base += base;
        featuredTotals[featured.key].sgst += sgst;
        featuredTotals[featured.key].cgst += cgst;
      } else {
        remainingAddonsBase += base;
        remainingAddonsSgst += sgst;
        remainingAddonsCgst += cgst;
      }
    });

    FEATURED_ADDON_SERVICES.forEach(({ key }) => {
      accRow[`acc_${key}_base`] = featuredTotals[key].base || null;
      accRow[`acc_${key}_sgst`] = featuredTotals[key].sgst || null;
      accRow[`acc_${key}_cgst`] = featuredTotals[key].cgst || null;
    });
    accRow.acc_remaining_addons_base = remainingAddonsBase || null;
    accRow.acc_remaining_addons_sgst = remainingAddonsSgst || null;
    accRow.acc_remaining_addons_cgst = remainingAddonsCgst || null;

    // Total addons base across all addon types (for acc_total_base)
    const addonsBase = FEATURED_ADDON_SERVICES.reduce((sum, { key }) => sum + featuredTotals[key].base, 0) + remainingAddonsBase;

    // --- Total Base (Room + Food + Course + Addons) ---
    const totalBase = roomBase + foodBase + courseBase + addonsBase;
    accRow.acc_total_base = totalBase || null;

    // --- Total without tax (totalPrice/finalAmount minus all TAX-type PBs) ---
    const grandTotal = parseFloat(row.totalPrice) || parseFloat(row.finalAmount) || parseFloat(row.grandTotal) || parseFloat(row.totalAmount) || 0;
    accRow.acc_total_without_tax = grandTotal > 0 ? (grandTotal - totalTax) : null;

    // --- Balance & payments ---
    accRow.acc_balance = parseFloat(row.balanceDue) || null;
    accRow.acc_payment_total = parseFloat(row.totalPaid) || null;
    let cashTotal = 0;

    payments.forEach((payment, idx) => {
      const p = idx + 1;
      accRow[`acc_payment_${p}_amount`] = parseFloat(payment.amount) || null;
      accRow[`acc_payment_${p}_date`] = payment.paymentDate || payment.createdAt || null;
      accRow[`acc_payment_${p}_method`] = payment.method || null;
      if (payment.method && payment.method.toLowerCase() === 'cash') {
        cashTotal += parseFloat(payment.amount) || 0;
      }
    });
    accRow.acc_cash_total = cashTotal || null;
    for (let p = payments.length + 1; p <= maxPaymentsCount; p++) {
      accRow[`acc_payment_${p}_amount`] = null;
      accRow[`acc_payment_${p}_date`] = null;
      accRow[`acc_payment_${p}_method`] = null;
    }

    // --- Refunds ---
    refunds.forEach((refund, idx) => {
      const r = idx + 1;
      accRow[`acc_refund_${r}_amount`] = parseFloat(refund.amount) || null;
      accRow[`acc_refund_${r}_date`] = refund.refundDate || refund.createdAt || null;
      accRow[`acc_refund_${r}_method`] = refund.method || refund.refundMethod || null;
    });
    for (let r = refunds.length + 1; r <= maxRefundsCount; r++) {
      accRow[`acc_refund_${r}_amount`] = null;
      accRow[`acc_refund_${r}_date`] = null;
      accRow[`acc_refund_${r}_method`] = null;
    }

    return accRow;
  });

  return { accountsData, additionalColumns };
};

// Reports that list bookings or payments over time. They only ever load the months
// picked in the period selector (sent to the backend as `months=YYYY-MM,...`), so a
// report never pulls the whole booking history into the browser.
const MONTH_SCOPED_TABS = ['master-booking', 'course-participants', 'accounts', 'omg-experience', 'all-guests', 'payment-report', 'accounts-master'];
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MAX_SELECTED_MONTHS = 12;
const monthKey = (year, month) => `${year}-${String(month).padStart(2, '0')}`;
const monthLabel = (key) => {
  const [y, m] = key.split('-');
  return `${MONTH_LABELS[Number(m) - 1]} ${y}`;
};
const dayLabel = (iso) => {
  if (!iso) return '…';
  const [y, m, d] = iso.split('-');
  return `${Number(d)} ${MONTH_LABELS[Number(m) - 1]} ${y}`;
};
const MAX_RANGE_DAYS = 731;
// A loaded period is { mode: 'months', months: ['2026-09', ...] } or { mode: 'range', from, to }.
const periodQuery = (period) => {
  if (!period) return {};
  if (period.mode === 'range') return { from: period.from, to: period.to };
  return { months: period.months.join(',') };
};
const periodLabel = (period) => {
  if (!period) return '';
  return period.mode === 'range' ? `${dayLabel(period.from)} to ${dayLabel(period.to)}` : period.months.map(monthLabel).join(', ');
};
const periodKey = (period) => (!period ? '' : period.mode === 'range' ? `range:${period.from}..${period.to}` : `months:${period.months.join(',')}`);
const BOOKING_STATUS_OPTIONS = [
  ['PENDING', 'Pending'], ['CONFIRMED', 'Confirmed'], ['CHECKEDIN', 'Checked In'],
  ['CHECKEDOUT', 'Checked Out'], ['COMPLETED', 'Completed'], ['CANCELLED', 'Cancelled']
];
const BOOKING_PLATFORM_OPTIONS = [['BOOKING_ENGINE', 'Booking Engine'], ['STAFF_RESERVATION', 'Staff Reservation'], ['OTHERS', 'Others']];

const GUEST_LIST_TABS = ['omg-experience', 'course-participants', 'all-guests'];
const formatRupees = (v) => `${v < 0 ? '-' : ''}₹${Math.round(Math.abs(v)).toLocaleString('en-IN')}`;

/**
 * Summary of the guest-list rows on screen (so it follows the period, booking filters
 * and course filter). Each booking is counted once: a booking on two courses has two
 * rows. Guests per booking = the report's `guests` (recorded guest count, or the
 * course seats booked when larger).
 */
const buildGuestSummary = (tab, rows) => {
  const bookings = new Map();
  let enrolments = 0;
  const courses = new Set();
  rows.forEach((r) => {
    const isCourse = tab === 'course-participants' || r.guestType === 'Course Participant';
    if (isCourse) {
      enrolments += 1;
      if (r.course) courses.add(r.course);
    }
    const id = r.bookingId || r.bookingNumber;
    const prev = bookings.get(id);
    bookings.set(id, {
      guests: Math.max(prev ? prev.guests : 0, Number(r.guests ?? r.guestCount ?? 1) || 0),
      total: Number(r.totalAmount) || 0,
      paid: Number(r.paidAmount) || 0,
      balance: Number(r.balanceAmount) || 0,
      course: isCourse || (prev ? prev.course : false),
    });
  });
  const list = [...bookings.values()];
  const guests = (xs) => xs.reduce((sum, b) => sum + b.guests, 0);
  const money = [
    { label: 'Revenue', value: list.reduce((sum, b) => sum + b.total, 0), currency: true },
    { label: 'Paid', value: list.reduce((sum, b) => sum + b.paid, 0), currency: true },
    { label: 'Balance', value: list.reduce((sum, b) => sum + b.balance, 0), currency: true },
  ];
  if (tab === 'omg-experience') {
    return [{ label: 'OMG Experience bookings', value: list.length }, { label: 'Guests', value: guests(list) }, ...money];
  }
  if (tab === 'course-participants') {
    return [
      { label: 'Bookings', value: list.length },
      { label: 'Guests', value: guests(list) },
      { label: 'Course enrolments', value: enrolments },
      { label: 'Courses', value: courses.size },
      ...money,
    ];
  }
  const courseBookings = list.filter((b) => b.course);
  const omgBookings = list.filter((b) => !b.course);
  return [
    { label: 'Bookings', value: list.length },
    { label: 'Guests', value: guests(list) },
    { label: 'Course guests', value: guests(courseBookings) },
    { label: 'OMG Experience guests', value: guests(omgBookings) },
    { label: 'OMG Experience bookings', value: omgBookings.length },
    ...money,
  ];
};

/** Server summary objects as labelled items ("totalOMGBookings" -> "Total OMG Bookings"). */
const serverSummaryItems = (summary) =>
  Object.entries(summary || {})
    .filter(([, value]) => value === null || typeof value !== 'object')
    .map(([key, value]) => ({
      label: key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/([A-Z])([A-Z][a-z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase()),
      value,
      currency: typeof value === 'number' && /revenue|amount|paid|balance|refund|gst|tax|price|fee/i.test(key),
    }));
// A dynamic column is worth showing only if some row has a real value in it
// (add-on groups for unused services are all 0 / null).
const isMeaningful = (v) => !(v === null || v === undefined || v === '' || v === 0 || v === '0' || v === '-' || v === '[]' || v === '{}');
const parseList = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.startsWith('[')) {
    try { return JSON.parse(value); } catch { return []; }
  }
  return [];
};

const NewReports = ({ user }) => {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState('master-booking');
  const today = new Date();
  // Period selector: months ticked in the UI vs months whose data is on screen
  const [periodYear, setPeriodYear] = useState(today.getFullYear());
  const [selectedMonths, setSelectedMonths] = useState([monthKey(today.getFullYear(), today.getMonth() + 1)]);
  const [periodMode, setPeriodMode] = useState('months'); // 'months' | 'range'
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  const [appliedPeriod, setAppliedPeriod] = useState(null); // the period whose data is on screen
  const [hideEmptyColumns, setHideEmptyColumns] = useState(true);
  const isMonthScoped = MONTH_SCOPED_TABS.includes(activeTab);
  const [reportData, setReportData] = useState([]);
  const [originalData, setOriginalData] = useState([]); // Store unfiltered data (after processing)
  const [rawData, setRawData] = useState([]); // Store raw data from backend (before any processing)
  const [dynamicColumns, setDynamicColumns] = useState([]);
  const [reportSummary, setReportSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [viewMode, setViewMode] = useState('expanded'); // 'expanded', 'merged', or 'accounts'
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0 });
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    status: '',
    paymentStatus: '',
    propertyId: '',
    courseId: '',
    date: ''
  });

  // Master Booking Report specific filters
  const [filterCourseName, setFilterCourseName] = useState('');
  const [filterRoomType, setFilterRoomType] = useState('');
  const [filterBedType, setFilterBedType] = useState('');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState('');
  const [filterBookingStatus, setFilterBookingStatus] = useState('');
  const [filterBookingPlatform, setFilterBookingPlatform] = useState('');
  const [filterOmgExperience, setFilterOmgExperience] = useState('');
  const [filterGender, setFilterGender] = useState('');
  const [filterPaymentDateStart, setFilterPaymentDateStart] = useState('');
  const [filterPaymentDateEnd, setFilterPaymentDateEnd] = useState('');

  // Payment report specific filters
  const [paymentFilterCheckInStart, setPaymentFilterCheckInStart] = useState('');
  const [paymentFilterCheckInEnd, setPaymentFilterCheckInEnd] = useState('');
  const [paymentFilterAmountMin, setPaymentFilterAmountMin] = useState('');
  const [paymentFilterAmountMax, setPaymentFilterAmountMax] = useState('');
  const [paymentFilterGuestName, setPaymentFilterGuestName] = useState('');
  const [paymentFilterAmountSearch, setPaymentFilterAmountSearch] = useState('');
  const [paymentFilterStatus, setPaymentFilterStatus] = useState('');
  const [paymentFilterMethod, setPaymentFilterMethod] = useState('');
  const [paymentOriginalData, setPaymentOriginalData] = useState([]);

  // Occupancy monthly/yearly specific filters
  const [occupancyYear, setOccupancyYear] = useState(new Date().getFullYear());
  const [occupancyMonth, setOccupancyMonth] = useState(new Date().getMonth() + 1);

  // Accounts Master report specific filters
  const [acctMasterFilterStatus, setAcctMasterFilterStatus] = useState('');
  const [acctMasterFilterPaymentStatus, setAcctMasterFilterPaymentStatus] = useState('');
  const [acctMasterFilterPlatform, setAcctMasterFilterPlatform] = useState('');

  // createdAt date range filter (booking creation date) - used by master-booking, omg-experience, all-guests
  const [filterCreatedAtStart, setFilterCreatedAtStart] = useState('');
  const [filterCreatedAtEnd, setFilterCreatedAtEnd] = useState('');

  // Dynamic options extracted from data
  const [courseOptions, setCourseOptions] = useState([]);
  const [roomTypeOptions, setRoomTypeOptions] = useState([]);
  const [bedTypeOptions, setBedTypeOptions] = useState([]);

  // All courses fetched from backend for course-participants dropdown
  const [allCourses, setAllCourses] = useState([]);

  // All addon services fetched from backend for accounts view grouping
  const [allAddonServices, setAllAddonServices] = useState([]);
  
  // Selected course name for course-participants client-side filter
  const [filterCourseParticipant, setFilterCourseParticipant] = useState('');
  
  const [showFiltersDropdown, setShowFiltersDropdown] = useState(false);

  useEffect(() => {
    setShowFiltersDropdown(!isMobile);
  }, [isMobile]);

  // Fetch addon services on mount for accounts view
  useEffect(() => {
    const fetchAddonServices = async () => {
      try {
        const response = await addonsService.getAll();
        const services = response.data?.services || response.data || [];
        setAllAddonServices(services.filter(s => s.isActive !== false));
      } catch (e) {
        console.warn('Failed to fetch addon services:', e);
      }
    };
    fetchAddonServices();
  }, []);

  // Extract unique options from nested arrays
  const extractUniqueOptions = (data, arrayKey, fieldKey) => {
    const uniqueValues = new Set();
    
    data.forEach(row => {
      if (row[arrayKey]) {
        try {
          const parsed = typeof row[arrayKey] === 'string' ? JSON.parse(row[arrayKey]) : row[arrayKey];
          if (Array.isArray(parsed)) {
            parsed.forEach(item => {
              if (item[fieldKey]) {
                uniqueValues.add(item[fieldKey]);
              }
            });
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
    });
    
    return Array.from(uniqueValues).sort();
  };

  // Apply all active filters to the data
  const applyFilters = (data) => {
    if (!data || data.length === 0) return data;

    // raw (unflattened) booking per booking number, for filters that need its arrays
    const rawByNumber = new Map(rawData.map(r => [r.bookingNumber, r]));

    const filtered = data.filter(row => {
      // Course Name filter - check both expanded and merged formats (skip in accounts view)
      if (filterCourseName && viewMode !== 'accounts') {
        let hasCourse = false;
        
        if (viewMode === 'merged') {
          // Merged view: check courses_courseName (comma-separated)
          const courseValue = row['courses_courseName'];
          if (courseValue) {
            const courses = String(courseValue).split(',').map(c => c.trim());
            hasCourse = courses.includes(filterCourseName);
          }
        } else {
          // Expanded view: check courses_1_courseName, courses_2_courseName, etc.
          const courseColumns = Object.keys(row).filter(key => key.match(/^courses_\d+_courseName$/));
          hasCourse = courseColumns.some(col => row[col] === filterCourseName);
        }

        if (!hasCourse) return false;
      }
      
      // Room Type filter - check both expanded and merged formats (skip in accounts view)
      if (filterRoomType && viewMode !== 'accounts') {
        let hasRoomType = false;
        
        if (viewMode === 'merged') {
          // Merged view: check rooms_roomTypeName (comma-separated)
          const roomTypeValue = row['rooms_roomTypeName'];
          if (roomTypeValue) {
            const roomTypes = String(roomTypeValue).split(',').map(r => r.trim());
            hasRoomType = roomTypes.includes(filterRoomType);
          }
        } else {
          // Expanded view: check rooms_1_roomTypeName, rooms_2_roomTypeName, etc.
          const roomColumns = Object.keys(row).filter(key => key.match(/^rooms_\d+_roomTypeName$/));
          hasRoomType = roomColumns.some(col => row[col] === filterRoomType);
        }
        
        if (!hasRoomType) return false;
      }
      
      // Bed Type filter - check both expanded and merged formats (skip in accounts view)
      if (filterBedType && viewMode !== 'accounts') {
        let hasBedType = false;
        
        if (viewMode === 'merged') {
          // Merged view: check beds_bedTypeName (comma-separated)
          const bedTypeValue = row['beds_bedTypeName'];
          if (bedTypeValue) {
            const bedTypes = String(bedTypeValue).split(',').map(b => b.trim());
            hasBedType = bedTypes.includes(filterBedType);
          }
        } else {
          // Expanded view: check beds_1_bedTypeName, beds_2_bedTypeName, etc.
          const bedColumns = Object.keys(row).filter(key => key.match(/^beds_\d+_bedTypeName$/));
          hasBedType = bedColumns.some(col => row[col] === filterBedType);
        }
        
        if (!hasBedType) return false;
      }
      
      // Payment Status filter
      if (filterPaymentStatus) {
        const totalPaid = parseFloat(row.totalPaid || 0);
        const finalAmount = parseFloat(row.finalAmount || 0);
        let paymentStatus = 'PENDING';
        
        if (totalPaid >= finalAmount && finalAmount > 0) {
          paymentStatus = 'PAID';
        } else if (totalPaid > 0) {
          paymentStatus = 'PARTIAL';
        }
        
        if (paymentStatus !== filterPaymentStatus) return false;
      }
      
      // Booking Status filter
      if (filterBookingStatus && row.bookingStatus !== filterBookingStatus) {
        return false;
      }
      
      // Booking Platform filter
      if (filterBookingPlatform && row.bookingPlatform !== filterBookingPlatform) {
        return false;
      }
      
      // OMG Experience filter: a booking without any course (shared bed or private room),
      // the same definition the OMG Experience report uses (skip in accounts view)
      if (filterOmgExperience && viewMode !== 'accounts') {
        const rawRow = rawByNumber.get(row.bookingNumber);
        const isOmg = parseList(rawRow ? rawRow.courses : row.courses).length === 0;
        if (filterOmgExperience === 'YES' && !isOmg) return false;
        if (filterOmgExperience === 'NO' && isOmg) return false;
      }
      
      // Gender filter
      if (filterGender && row.userGender !== filterGender) {
        return false;
      }
      
      // Payment Date filter - check if booking has any payment within the date range
      // This filter works on the raw payment data stored in the row
      if (filterPaymentDateStart || filterPaymentDateEnd) {
        const payStart = filterPaymentDateStart ? new Date(filterPaymentDateStart) : null;
        const payEnd = filterPaymentDateEnd ? new Date(filterPaymentDateEnd) : null;
        if (payStart) payStart.setHours(0, 0, 0, 0);
        if (payEnd) payEnd.setHours(23, 59, 59, 999);
        
        // Find raw data row to get original payments array
        const rawRow = rawByNumber.get(row.bookingNumber);
        let hasMatchingPayment = false;
        
        if (rawRow && rawRow.payments) {
          try {
            const payments = typeof rawRow.payments === 'string' ? JSON.parse(rawRow.payments) : rawRow.payments;
            if (Array.isArray(payments)) {
              hasMatchingPayment = payments.some(payment => {
                const pDate = payment.paymentDate || payment.createdAt;
                if (!pDate) return false;
                const paymentDate = new Date(pDate);
                if (isNaN(paymentDate.getTime())) return false;
                if (payStart && paymentDate < payStart) return false;
                if (payEnd && paymentDate > payEnd) return false;
                return true;
              });
            }
          } catch (e) { /* ignore */ }
        }
        
        if (!hasMatchingPayment) return false;
      }
      
      return true;
    });
    
    return filtered;
  };

  // Apply payment report specific filters (client-side)
  const applyPaymentFilters = (data) => {
    if (!data || data.length === 0) return data;
    
    return data.filter(row => {
      // Check-in/Check-out date range filter
      if (paymentFilterCheckInStart || paymentFilterCheckInEnd) {
        const ciStart = paymentFilterCheckInStart ? new Date(paymentFilterCheckInStart) : null;
        const ciEnd = paymentFilterCheckInEnd ? new Date(paymentFilterCheckInEnd) : null;
        if (ciStart) ciStart.setHours(0, 0, 0, 0);
        if (ciEnd) ciEnd.setHours(23, 59, 59, 999);
        
        const checkIn = row.checkIn ? new Date(row.checkIn) : null;
        const checkOut = row.checkOut ? new Date(row.checkOut) : null;
        
        if (!checkIn && !checkOut) return false;
        const bookingStart = checkIn || checkOut;
        const bookingEnd = checkOut || checkIn;
        
        if (ciStart && ciEnd) {
          if (!(bookingStart <= ciEnd && bookingEnd >= ciStart)) return false;
        } else if (ciStart) {
          if (bookingEnd < ciStart) return false;
        } else if (ciEnd) {
          if (bookingStart > ciEnd) return false;
        }
      }
      
      // Amount range filter
      if (paymentFilterAmountMin || paymentFilterAmountMax) {
        const amount = parseFloat(row.amount) || 0;
        if (paymentFilterAmountMin && amount < parseFloat(paymentFilterAmountMin)) return false;
        if (paymentFilterAmountMax && amount > parseFloat(paymentFilterAmountMax)) return false;
      }
      
      // Guest name search (case-insensitive partial match)
      if (paymentFilterGuestName) {
        const name = (row.guestName || '').toLowerCase();
        if (!name.includes(paymentFilterGuestName.toLowerCase())) return false;
      }
      
      // Exact amount search
      if (paymentFilterAmountSearch) {
        const searchAmt = parseFloat(paymentFilterAmountSearch);
        const amount = parseFloat(row.amount) || 0;
        if (!isNaN(searchAmt) && amount !== searchAmt) return false;
      }
      
      // Payment method filter
      if (paymentFilterMethod) {
        if ((row.method || '').toLowerCase() !== paymentFilterMethod.toLowerCase()) return false;
      }
      
      return true;
    });
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilterCourseName('');
    setFilterRoomType('');
    setFilterBedType('');
    setFilterPaymentStatus('');
    setFilterBookingStatus('');
    setFilterBookingPlatform('');
    setFilterOmgExperience('');
    setFilterGender('');
    setFilterPaymentDateStart('');
    setFilterPaymentDateEnd('');
    setPaymentFilterCheckInStart('');
    setPaymentFilterCheckInEnd('');
    setPaymentFilterAmountMin('');
    setPaymentFilterAmountMax('');
    setPaymentFilterGuestName('');
    setPaymentFilterAmountSearch('');
    setPaymentFilterStatus('');
    setPaymentFilterMethod('');
    setFilters({
      startDate: '',
      endDate: '',
      status: '',
      paymentStatus: '',
      propertyId: '',
      courseId: '',
      date: format(new Date(), 'yyyy-MM-dd')
    });
    
    // Restore original data
    if (originalData.length > 0) {
      setReportData(originalData);
      console.log('[master-booking] Filters cleared, restored original data:', originalData.length, 'records');
    }
    
    toast.info('All filters cleared');
  };

  // Report configuration
  const reportTabs = [
    {
      id: 'master-booking',
      label: 'Master Booking',
      description: 'Comprehensive booking data with all relationships'
    },
    {
      id: 'occupancy',
      label: 'Occupancy',
      description: 'Room and bed occupancy analysis'
    },
    {
      id: 'course-participants',
      label: 'Course Participants',
      description: 'Course enrollment and participant details'
    },
    {
      id: 'accounts',
      label: 'Accounts & GST',
      description: 'Financial report with GST breakdown'
    },
    {
      id: 'check-in',
      label: 'Check-in',
      description: 'Daily check-in operations'
    },
    {
      id: 'check-out',
      label: 'Check-out',
      description: 'Daily check-out operations'
    },
    {
      id: 'omg-experience',
      label: 'OMG Experience',
      description: 'Stays without a course, in shared beds or private rooms. Guests = recorded guests, or beds/rooms held at the same time if more'
    },
    {
      id: 'all-guests',
      label: 'All Guests',
      description: 'Every guest: course participants plus OMG Experience stays (no course, shared or private)'
    },
    {
      id: 'services-inventory',
      label: 'Inventory',
      description: 'Daily stock movement, inventory tracking & sales analysis'
    },
    {
      id: 'daily-summary',
      label: 'Daily Summary',
      description: 'High-level operational overview'
    },
    {
      id: 'occupancy-monthly',
      label: 'Occupancy Monthly',
      description: 'Per-room daily occupancy for a specific month'
    },
    {
      id: 'occupancy-yearly',
      label: 'Occupancy Yearly',
      description: 'Per-room monthly-average occupancy for a full year'
    },
    {
      id: 'payment-report',
      label: 'Payment Report',
      description: 'Detailed payment-level data with guest info and tax breakdown'
    },
    {
      id: 'accounts-master',
      label: 'Accounts Master',
      description: 'Flat addon columns for spreadsheet-based tax & revenue analysis'
    }
  ];

  // Column definitions for each report type
  const getColumnsForReport = (reportType) => {
    switch (reportType) {
      case 'master-booking':
        return [
          { key: 'bookingNumber', label: 'Booking #', minWidth: '120px' },
          { key: 'bookingStatus', label: 'Status', minWidth: '100px' },
          { key: 'userName', label: 'Guest Name', minWidth: '150px' },
          { key: 'userEmail', label: 'Email', minWidth: '180px' },
          { key: 'userPhone', label: 'Phone', minWidth: '120px' },
          { key: 'checkInDate', label: 'Check-in', type: 'date', minWidth: '110px' },
          { key: 'checkOutDate', label: 'Check-out', type: 'date', minWidth: '110px' },
          { key: 'guestCount', label: 'Guests', align: 'center', minWidth: '70px' },
          { key: 'roomsCount', label: 'Rooms', align: 'center', minWidth: '70px' },
          { key: 'bedsCount', label: 'Beds', align: 'center', minWidth: '70px' },
          { key: 'coursesCount', label: 'Courses', align: 'center', minWidth: '80px' },
          { key: 'addOnsCount', label: 'Add-ons', align: 'center', minWidth: '80px' },
          { key: 'paymentsCount', label: 'Payments', align: 'center', minWidth: '80px' },
          { key: 'refundsCount', label: 'Refunds', align: 'center', minWidth: '80px' },
          { key: 'finalAmount', label: 'Total', type: 'currency', align: 'right', minWidth: '110px' },
          { key: 'totalPaid', label: 'Paid', type: 'currency', align: 'right', minWidth: '110px' },
          { key: 'balanceDue', label: 'Balance', type: 'currency', align: 'right', minWidth: '110px' }
          // Dynamic columns for rooms, beds, courses, addOns, payments, refunds will be added automatically
        ];

      case 'occupancy':
        return [
          { key: 'propertyName', label: 'Property', minWidth: '150px' },
          { key: 'roomNumber', label: 'Room #', minWidth: '100px' },
          { key: 'roomType', label: 'Room Type', minWidth: '140px' },
          { key: 'isShared', label: 'Shared?', minWidth: '80px', formatter: (v) => v === null ? '-' : (v ? 'Yes' : 'No') },
          { key: 'capacity', label: 'Capacity', align: 'center', minWidth: '90px' },
          { key: 'todayOccupancy', label: 'Today %', align: 'right', minWidth: '100px', formatter: (v) => v != null ? `${v.toFixed(1)}%` : '-' },
          { key: 'monthTillNowOccupancy', label: 'MTD %', align: 'right', minWidth: '100px', formatter: (v) => v != null ? `${v.toFixed(1)}%` : '-' },
          { key: 'previousMonthOccupancy', label: 'Prev Month %', align: 'right', minWidth: '120px', formatter: (v) => v != null ? `${v.toFixed(1)}%` : '-' },
          { key: 'expectedRemainingMonthOccupancy', label: 'Forecast %', align: 'right', minWidth: '110px', formatter: (v) => v != null ? `${v.toFixed(1)}%` : '-' }
        ];

      case 'course-participants':
        return [
          { key: 'course', label: 'Course', minWidth: '180px' },
          { key: 'name', label: 'Name', minWidth: '150px' },
          { key: 'email', label: 'Email', minWidth: '180px' },
          { key: 'phone', label: 'Phone', minWidth: '120px' },
          { key: 'quantity', label: 'Quantity', align: 'center', minWidth: '80px' },
          { key: 'guests', label: 'Guests', align: 'center', minWidth: '80px' },
          { key: 'bookingNumber', label: 'Booking #', minWidth: '110px' },
          { key: 'bookingStatus', label: 'Status', minWidth: '110px' },
          { key: 'bookingId', label: 'Booking ID', minWidth: '120px' },
          { key: 'bookingPlatform', label: 'Platform', minWidth: '130px', formatter: (v) => {
            const icons = { BOOKING_ENGINE: '', ADMIN: '', WALK_IN: '', PHONE: '', EMAIL: '' };
            return `${icons[v] || ''} ${v?.replace('_', ' ') || '-'}`;
          }},
          { key: 'checkInDate', label: 'Check-in', type: 'date', minWidth: '110px' },
          { key: 'checkOutDate', label: 'Check-out', type: 'date', minWidth: '110px' },
          { key: 'accommodationType', label: 'Stay', minWidth: '110px' },
          { key: 'accommodationDetails', label: 'Accommodation', minWidth: '200px' },
          { key: 'totalAmount', label: 'Total', type: 'currency', align: 'right', minWidth: '110px' },
          { key: 'paidAmount', label: 'Paid', type: 'currency', align: 'right', minWidth: '110px' },
          { key: 'balanceAmount', label: 'Balance', type: 'currency', align: 'right', minWidth: '110px' }
        ];

      case 'accounts':
        return [
          { key: 'bookingNumber', label: 'Booking #', minWidth: '120px' },
          { key: 'bookingDate', label: 'Date', type: 'date', minWidth: '110px' },
          { key: 'customerName', label: 'Customer', minWidth: '150px' },
          { key: 'bookingStatus', label: 'Status', minWidth: '100px' },
          { key: 'paymentStatus', label: 'Payment', minWidth: '100px' },
          { key: 'baseAmount', label: 'Base Amount', type: 'currency', align: 'right', minWidth: '120px' },
          { key: 'CGST', label: 'CGST', type: 'currency', align: 'right', minWidth: '100px' },
          { key: 'SGST', label: 'SGST', type: 'currency', align: 'right', minWidth: '100px' },
          { key: 'IGST', label: 'IGST', type: 'currency', align: 'right', minWidth: '100px' },
          { key: 'totalGST', label: 'Total GST', type: 'currency', align: 'right', minWidth: '110px' },
          { key: 'finalAmount', label: 'Final Amount', type: 'currency', align: 'right', minWidth: '120px' },
          { key: 'totalPaid', label: 'Paid', type: 'currency', align: 'right', minWidth: '110px' },
          { key: 'balanceDue', label: 'Due', type: 'currency', align: 'right', minWidth: '100px' }
          // Dynamic columns for allTaxes will be added automatically
        ];

      case 'check-in':
        return [
          { key: 'bookingNumber', label: 'Booking #', minWidth: '120px' },
          { key: 'guestName', label: 'Guest Name', minWidth: '150px' },
          { key: 'guestEmail', label: 'Email', minWidth: '180px' },
          { key: 'guestPhone', label: 'Phone', minWidth: '120px' },
          { key: 'checkInDate', label: 'Check-in Date', type: 'datetime', minWidth: '140px' },
          { key: 'nights', label: 'Nights', align: 'center', minWidth: '70px' },
          { key: 'roomsCount', label: 'Rooms', align: 'center', minWidth: '70px' },
          { key: 'bedsCount', label: 'Beds', align: 'center', minWidth: '70px' },
          { key: 'checkinFormStatus', label: 'Form Status', minWidth: '110px' },
          { key: 'paymentComplete', label: 'Payment', minWidth: '90px' },
          { key: 'readyForCheckIn', label: 'Ready?', minWidth: '80px' },
          { key: 'totalAmount', label: 'Amount', type: 'currency', align: 'right', minWidth: '110px' },
          { key: 'balanceDue', label: 'Due', type: 'currency', align: 'right', minWidth: '100px' }
          // Dynamic columns for rooms will be added automatically
        ];

      case 'check-out':
        return [
          { key: 'bookingNumber', label: 'Booking #', minWidth: '120px' },
          { key: 'guestName', label: 'Guest Name', minWidth: '150px' },
          { key: 'guestEmail', label: 'Email', minWidth: '180px' },
          { key: 'checkOutDate', label: 'Check-out Date', type: 'datetime', minWidth: '140px' },
          { key: 'totalNights', label: 'Nights', align: 'center', minWidth: '70px' },
          { key: 'roomsCount', label: 'Rooms', align: 'center', minWidth: '70px' },
          { key: 'addOnsCount', label: 'Add-ons', align: 'center', minWidth: '80px' },
          { key: 'totalAmount', label: 'Total', type: 'currency', align: 'right', minWidth: '110px' },
          { key: 'totalPaid', label: 'Paid', type: 'currency', align: 'right', minWidth: '110px' },
          { key: 'totalRefunded', label: 'Refunded', type: 'currency', align: 'right', minWidth: '110px' },
          { key: 'balanceDue', label: 'Due', type: 'currency', align: 'right', minWidth: '100px' },
          { key: 'settlementStatus', label: 'Settlement', minWidth: '100px' }
          // Dynamic columns for rooms, addOns will be added automatically
        ];

      case 'omg-experience':
        return [
          { key: 'name', label: 'Name', minWidth: '150px' },
          { key: 'email', label: 'Email', minWidth: '180px' },
          { key: 'phone', label: 'Phone', minWidth: '120px' },
          { key: 'quantity', label: 'Quantity', align: 'center', minWidth: '80px' },
          { key: 'guests', label: 'Guests', align: 'center', minWidth: '80px' },
          { key: 'bookingNumber', label: 'Booking #', minWidth: '110px' },
          { key: 'bookingStatus', label: 'Status', minWidth: '110px' },
          { key: 'bookingId', label: 'Booking ID', minWidth: '120px' },
          { key: 'bookingPlatform', label: 'Platform', minWidth: '130px', formatter: (v) => {
            const icons = { BOOKING_ENGINE: '', ADMIN: '', WALK_IN: '', PHONE: '', EMAIL: '' };
            return `${icons[v] || ''} ${v?.replace('_', ' ') || '-'}`;
          }},
          { key: 'checkInDate', label: 'Check-in', type: 'date', minWidth: '110px' },
          { key: 'checkOutDate', label: 'Check-out', type: 'date', minWidth: '110px' },
          { key: 'accommodationType', label: 'Stay', minWidth: '110px' },
          { key: 'accommodationDetails', label: 'Accommodation', minWidth: '200px' },
          { key: 'totalAmount', label: 'Total', type: 'currency', align: 'right', minWidth: '110px' },
          { key: 'paidAmount', label: 'Paid', type: 'currency', align: 'right', minWidth: '110px' },
          { key: 'balanceAmount', label: 'Balance', type: 'currency', align: 'right', minWidth: '110px' }
        ];

      case 'all-guests':
        return [
          { key: 'guestType', label: 'Guest Type', minWidth: '160px' },
          { key: 'course', label: 'Course', minWidth: '180px' },
          { key: 'name', label: 'Name', minWidth: '150px' },
          { key: 'email', label: 'Email', minWidth: '180px' },
          { key: 'phone', label: 'Phone', minWidth: '120px' },
          { key: 'quantity', label: 'Quantity', align: 'center', minWidth: '80px' },
          { key: 'guests', label: 'Guests', align: 'center', minWidth: '80px' },
          { key: 'bookingNumber', label: 'Booking #', minWidth: '110px' },
          { key: 'bookingStatus', label: 'Status', minWidth: '110px' },
          { key: 'bookingId', label: 'Booking ID', minWidth: '120px' },
          { key: 'bookingPlatform', label: 'Platform', minWidth: '130px', formatter: (v) => {
            const icons = { BOOKING_ENGINE: '', ADMIN: '', WALK_IN: '', PHONE: '', EMAIL: '' };
            return `${icons[v] || ''} ${v?.replace('_', ' ') || '-'}`;
          }},
          { key: 'checkInDate', label: 'Check-in', type: 'date', minWidth: '110px' },
          { key: 'checkOutDate', label: 'Check-out', type: 'date', minWidth: '110px' },
          { key: 'accommodationType', label: 'Stay', minWidth: '110px' },
          { key: 'accommodationDetails', label: 'Accommodation', minWidth: '200px' },
          { key: 'totalAmount', label: 'Total', type: 'currency', align: 'right', minWidth: '110px' },
          { key: 'paidAmount', label: 'Paid', type: 'currency', align: 'right', minWidth: '110px' },
          { key: 'balanceAmount', label: 'Balance', type: 'currency', align: 'right', minWidth: '110px' }
        ];

      case 'services-inventory':
        return [
          { key: 'itemName', label: 'Item Name', minWidth: '180px' },
          { key: 'sku', label: 'SKU', minWidth: '120px' },
          { key: 'serviceName', label: 'Service', minWidth: '150px' },
          { key: 'unitPrice', label: 'Unit Price', type: 'currency', align: 'right', minWidth: '110px' },
          { key: 'openingStock', label: 'Opening Stock', align: 'center', minWidth: '110px' },
          { key: 'additionsInPeriod', label: 'Added', align: 'center', minWidth: '80px' },
          { key: 'soldInPeriod', label: 'Sold', align: 'center', minWidth: '80px' },
          { key: 'closingStock', label: 'Closing Stock', align: 'center', minWidth: '110px' },
          { key: 'inventoryStatus', label: 'Status', minWidth: '100px', formatter: (v) => {
            const colors = { IN_STOCK: '', LOW_STOCK: '', OUT_OF_STOCK: '' };
            return `${colors[v] || ''} ${v?.replace('_', ' ') || '-'}`;
          }},
          { key: 'stockAlert', label: 'Alert', align: 'center', minWidth: '70px', formatter: (v) => v ? '' : '✓' },
          { key: 'minStockLevel', label: 'Min Stock', align: 'center', minWidth: '90px' },
          { key: 'revenueInPeriod', label: 'Period Revenue', type: 'currency', align: 'right', minWidth: '130px' },
          { key: 'transactionsInPeriod', label: 'Transactions', align: 'center', minWidth: '100px' },
          { key: 'averageQuantityPerTransaction', label: 'Avg Qty/Txn', align: 'center', minWidth: '100px', formatter: (v) => v?.toFixed(2) || '-' },
          { key: 'averageRevenuePerTransaction', label: 'Avg ₹/Txn', type: 'currency', align: 'right', minWidth: '110px' },
          { key: 'isActive', label: 'Active', align: 'center', minWidth: '70px', formatter: (v) => v ? '✓' : '✗' },
          { key: 'transactions', label: 'Transaction Details', sortable: false }
        ];

      case 'daily-summary':
        return [
          { key: 'metric', label: 'Metric', minWidth: '200px' },
          { key: 'value', label: 'Value', align: 'right', minWidth: '150px' }
        ];

      case 'occupancy-monthly':
        // Base columns + day columns will be added dynamically
        return [
          { key: 'propertyName', label: 'Property', minWidth: '150px' },
          { key: 'roomNumber', label: 'Room #', minWidth: '100px' },
          { key: 'roomType', label: 'Room Type', minWidth: '140px' },
          { key: 'isShared', label: 'Shared?', minWidth: '80px', formatter: (v) => v === null ? '-' : (v ? 'Yes' : 'No') },
          { key: 'capacity', label: 'Capacity', align: 'center', minWidth: '80px' },
          { key: 'averageOccupancy', label: 'Avg %', align: 'right', minWidth: '90px', formatter: (v) => v != null ? `${v.toFixed(1)}%` : '-' }
        ];

      case 'occupancy-yearly':
        return [
          { key: 'propertyName', label: 'Property', minWidth: '150px' },
          { key: 'roomNumber', label: 'Room #', minWidth: '100px' },
          { key: 'roomType', label: 'Room Type', minWidth: '140px' },
          { key: 'isShared', label: 'Shared?', minWidth: '80px', formatter: (v) => v === null ? '-' : (v ? 'Yes' : 'No') },
          { key: 'capacity', label: 'Capacity', align: 'center', minWidth: '80px' },
          { key: 'jan', label: 'Jan %', align: 'right', minWidth: '80px', formatter: (v) => v != null ? `${v.toFixed(1)}%` : '-' },
          { key: 'feb', label: 'Feb %', align: 'right', minWidth: '80px', formatter: (v) => v != null ? `${v.toFixed(1)}%` : '-' },
          { key: 'mar', label: 'Mar %', align: 'right', minWidth: '80px', formatter: (v) => v != null ? `${v.toFixed(1)}%` : '-' },
          { key: 'apr', label: 'Apr %', align: 'right', minWidth: '80px', formatter: (v) => v != null ? `${v.toFixed(1)}%` : '-' },
          { key: 'may', label: 'May %', align: 'right', minWidth: '80px', formatter: (v) => v != null ? `${v.toFixed(1)}%` : '-' },
          { key: 'jun', label: 'Jun %', align: 'right', minWidth: '80px', formatter: (v) => v != null ? `${v.toFixed(1)}%` : '-' },
          { key: 'jul', label: 'Jul %', align: 'right', minWidth: '80px', formatter: (v) => v != null ? `${v.toFixed(1)}%` : '-' },
          { key: 'aug', label: 'Aug %', align: 'right', minWidth: '80px', formatter: (v) => v != null ? `${v.toFixed(1)}%` : '-' },
          { key: 'sep', label: 'Sep %', align: 'right', minWidth: '80px', formatter: (v) => v != null ? `${v.toFixed(1)}%` : '-' },
          { key: 'oct', label: 'Oct %', align: 'right', minWidth: '80px', formatter: (v) => v != null ? `${v.toFixed(1)}%` : '-' },
          { key: 'nov', label: 'Nov %', align: 'right', minWidth: '80px', formatter: (v) => v != null ? `${v.toFixed(1)}%` : '-' },
          { key: 'dec', label: 'Dec %', align: 'right', minWidth: '80px', formatter: (v) => v != null ? `${v.toFixed(1)}%` : '-' },
          { key: 'yearlyAverage', label: 'Yearly Avg %', align: 'right', minWidth: '110px', formatter: (v) => v != null ? `${v.toFixed(1)}%` : '-' }
        ];

      case 'payment-report':
        return [
          { key: 'paymentNumber', label: 'Payment #', minWidth: '140px' },
          { key: 'bookingNumber', label: 'Booking #', minWidth: '120px' },
          { key: 'guestName', label: 'Guest Name', minWidth: '150px' },
          { key: 'guestEmail', label: 'Email', minWidth: '180px' },
          { key: 'guestPhone', label: 'Phone', minWidth: '120px' },
          { key: 'paymentDate', label: 'Payment Date', type: 'datetime', minWidth: '140px' },
          { key: 'amount', label: 'Amount', type: 'currency', align: 'right', minWidth: '110px' },
          { key: 'baseAmount', label: 'Base Amount', type: 'currency', align: 'right', minWidth: '120px' },
          { key: 'method', label: 'Method', minWidth: '100px' },
          { key: 'status', label: 'Status', minWidth: '100px' },
          { key: 'transactionId', label: 'Transaction ID', minWidth: '160px' },
          { key: 'cgst', label: 'CGST', type: 'currency', align: 'right', minWidth: '90px' },
          { key: 'sgst', label: 'SGST', type: 'currency', align: 'right', minWidth: '90px' },
          { key: 'igst', label: 'IGST', type: 'currency', align: 'right', minWidth: '90px' },
          { key: 'totalTax', label: 'Total Tax', type: 'currency', align: 'right', minWidth: '100px' },
          { key: 'totalRefunded', label: 'Refunded', type: 'currency', align: 'right', minWidth: '100px' },
          { key: 'netAmount', label: 'Net Amount', type: 'currency', align: 'right', minWidth: '110px' },
          { key: 'checkIn', label: 'Check-in', type: 'date', minWidth: '110px' },
          { key: 'checkOut', label: 'Check-out', type: 'date', minWidth: '110px' },
          { key: 'bookingStatus', label: 'Booking Status', minWidth: '120px' },
          { key: 'numberOfRooms', label: 'Rooms', align: 'center', minWidth: '70px' },
          { key: 'numberOfBeds', label: 'Beds', align: 'center', minWidth: '70px' },
          { key: 'accommodationDetails', label: 'Accommodation', minWidth: '200px' },
          { key: 'reconciled', label: 'Reconciled', minWidth: '100px', formatter: (v) => v ? '✓' : '✗' },
          { key: 'refundCount', label: 'Refunds', align: 'center', minWidth: '80px' }
        ];

      case 'accounts-master':
        return [
          { key: 'bookingNumber', label: 'Booking #', minWidth: '140px' },
          { key: 'bookingStatus', label: 'Status', minWidth: '100px' },
          { key: 'paymentStatus', label: 'Payment Status', minWidth: '120px' },
          { key: 'bookingPlatform', label: 'Platform', minWidth: '130px' },
          { key: 'checkInDate', label: 'Check-in', type: 'date', minWidth: '110px' },
          { key: 'checkOutDate', label: 'Check-out', type: 'date', minWidth: '110px' },
          { key: 'guestCount', label: 'Guests', align: 'center', minWidth: '70px' },
          { key: 'userName', label: 'Guest Name', minWidth: '150px' },
          { key: 'userEmail', label: 'Email', minWidth: '180px' },
          { key: 'userPhone', label: 'Phone', minWidth: '120px' },
          { key: 'totalPrice', label: 'Total Price', type: 'currency', align: 'right', minWidth: '110px' },
          { key: 'finalAmount', label: 'Final Amount', type: 'currency', align: 'right', minWidth: '120px' },
          { key: 'totalPaid', label: 'Paid', type: 'currency', align: 'right', minWidth: '110px' },
          { key: 'totalRefunded', label: 'Refunded', type: 'currency', align: 'right', minWidth: '100px' },
          { key: 'balanceDue', label: 'Balance Due', type: 'currency', align: 'right', minWidth: '110px' },
          { key: 'totalGST', label: 'Total GST', type: 'currency', align: 'right', minWidth: '100px' },
          { key: 'totalDiscount', label: 'Discount', type: 'currency', align: 'right', minWidth: '100px' },
          { key: 'totalFees', label: 'Fees', type: 'currency', align: 'right', minWidth: '90px' },
          { key: 'coursesTotalFee', label: 'Courses Fee', type: 'currency', align: 'right', minWidth: '110px' },
          { key: 'coursesTotalFinalAmount', label: 'Courses Final', type: 'currency', align: 'right', minWidth: '120px' },
          { key: 'coursesTotalQuantity', label: 'Courses Qty', align: 'center', minWidth: '100px' },
          { key: 'addOnsTotalPrice', label: 'Addons Total', type: 'currency', align: 'right', minWidth: '120px' },
          { key: 'addOnsTotalFinalAmount', label: 'Addons Final', type: 'currency', align: 'right', minWidth: '120px' }
          // Dynamic addon service columns (addon_*) are added automatically
        ];

      default:
        return [];
    }
  };

  const clearReport = () => {
    setReportData([]);
    setOriginalData([]);
    setRawData([]);
    setDynamicColumns([]);
    setReportSummary(null);
    setLoading(false);
  };

  // Fetch report data. Month-scoped reports load only the chosen period (default: the
  // period last loaded); button handlers pass a click event, hence the `mode` check.
  const fetchReport = async (periodOverride) => {
    const period = periodOverride && periodOverride.mode ? periodOverride : appliedPeriod;
    const monthScoped = MONTH_SCOPED_TABS.includes(activeTab);
    if (monthScoped && !period) {
      clearReport();
      return;
    }

    setLoading(true);
    setLoadingProgress({ current: 0, total: 0 });

    try {
      let allData = [];
      let response;
      const params = { ...filters };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      // Booking reports: the server returns only bookings whose stay overlaps the
      // chosen months or date range (payments: paid in that period), filtered there by
      // booking status / platform / created date.
      if (monthScoped) {
        Object.assign(params, periodQuery(period));
        delete params.startDate;
        delete params.endDate;
      }
      // Master Booking filters status and platform on the loaded rows (its own dropdowns)
      if (activeTab === 'master-booking') {
        delete params.status;
        delete params.bookingPlatform;
      }

      // Don't send courseId to backend for course-participants - we filter client-side
      if (activeTab === 'course-participants') {
        delete params.courseId;
      }

      // Add room ordering by default for all reports (except daily-summary)
      if (activeTab !== 'daily-summary') {
        params.order = 'room';
        console.log(`[${activeTab}] Room ordering applied: order=room`);
      }

      // Inject createdAt date range filter for reports that support it
      if (monthScoped) {
        if (filterCreatedAtStart) params.createdAtStart = filterCreatedAtStart;
        if (filterCreatedAtEnd) params.createdAtEnd = filterCreatedAtEnd;
      }

      switch (activeTab) {
        case 'master-booking':
          // Fetch all pages for master-booking
          console.log('[master-booking] Fetching page 1 to get total pages...');
          
          // First request to get pagination info
          const firstPageParams = { ...params, page: 1, limit: 250 };
          response = await reportsService.getMasterBooking(firstPageParams);
          
          if (!response.success) {
            throw new Error(response.message || 'Failed to fetch data');
          }
          
          // Add first page data
          allData = response.data || [];
          
          // Check if there are more pages
          if (response.pagination) {
            const { totalPages, total } = response.pagination;
            console.log(`[master-booking] Total pages: ${totalPages}, Total records: ${total}`);
            setLoadingProgress({ current: 1, total: totalPages });
            
            // Fetch remaining pages if any
            if (totalPages > 1) {
              console.log(`[master-booking] Fetching remaining ${totalPages - 1} pages...`);
              
              const pagePromises = [];
              for (let page = 2; page <= totalPages; page++) {
                const pageParams = { ...params, page, limit: 250 };
                pagePromises.push(
                  reportsService.getMasterBooking(pageParams).then(res => {
                    console.log(`[master-booking] Fetched page ${page}/${totalPages}`);
                    setLoadingProgress({ current: page, total: totalPages });
                    return res.data || [];
                  })
                );
              }
              
              // Wait for all pages to complete
              const remainingPages = await Promise.all(pagePromises);
              
              // Combine all data
              remainingPages.forEach(pageData => {
                allData = [...allData, ...pageData];
              });
              
              console.log(`[master-booking] All pages fetched. Total records: ${allData.length}`);
            }
          }
          
          response.data = allData;
          break;
          
        case 'occupancy':
          response = await reportsService.getOccupancy({ ...params, limit: 0 });
          break;
        case 'course-participants':
          response = await reportsService.getCourseParticipants({ ...params, limit: 0 });
          break;
        case 'accounts':
          response = await reportsService.getAccounts({ ...params, limit: 0 });
          break;
        case 'check-in':
          response = await reportsService.getCheckIn({ ...params, limit: 0 });
          break;
        case 'check-out':
          response = await reportsService.getCheckOut({ ...params, limit: 0 });
          break;
        case 'omg-experience':
          response = await reportsService.getOmgExperience({ ...params, limit: 0 });
          break;
        case 'all-guests': {
          const [omgResponse, courseResponse] = await Promise.all([
            reportsService.getOmgExperience({ ...params, limit: 0 }),
            reportsService.getCourseParticipants({ ...params, limit: 0 })
          ]);
          const omgData = (omgResponse.success ? omgResponse.data || [] : []).map(r => ({ ...r, guestType: 'OMG Experience' }));
          const courseData = (courseResponse.success ? courseResponse.data || [] : []).map(r => ({ ...r, guestType: 'Course Participant' }));
          response = {
            success: omgResponse.success || courseResponse.success,
            data: [...courseData, ...omgData],
            summary: omgResponse.summary || courseResponse.summary || null
          };
          break;
        }
        case 'services-inventory':
          response = await reportsService.getServicesInventory({ ...params, limit: 0 });
          break;
        case 'daily-summary':
          response = await reportsService.getDailySummary({ date: filters.date });
          // Transform daily summary into table format
          if (response.success && response.data) {
            const summaryData = response.data.summary;
            const tableData = [
              { metric: 'Total Active Bookings', value: summaryData.totalActiveBookings },
              { metric: 'Check-ins Today', value: summaryData.checkInsToday },
              { metric: 'Check-outs Today', value: summaryData.checkOutsToday },
              { metric: 'New Bookings Today', value: summaryData.newBookingsToday },
              { metric: 'Occupied Beds', value: summaryData.currentOccupancy?.occupiedBeds },
              { metric: 'Total Beds', value: summaryData.currentOccupancy?.totalBeds },
              { metric: 'Occupancy Rate', value: summaryData.currentOccupancy?.occupancyRate },
              { metric: 'Payments Today (Count)', value: summaryData.paymentsToday?.count },
              { metric: 'Payments Today (Amount)', value: `₹${summaryData.paymentsToday?.totalAmount?.toFixed(2)}` }
            ];
            setReportData(tableData);
            setReportSummary(null);
            setLoading(false);
            toast.success('Daily summary loaded successfully');
            return;
          }
          break;
        case 'occupancy-monthly':
          response = await reportsService.getMonthlyOccupancy({ year: occupancyYear, month: occupancyMonth });
          if (response.success && response.data) {
            // Generate dynamic day columns from metadata
            const totalDays = response.metadata?.totalDays || 31;
            const dayCols = [];
            for (let d = 1; d <= totalDays; d++) {
              const dayKey = `day_${String(d).padStart(2, '0')}`;
              dayCols.push({
                key: dayKey,
                label: `Day ${d}`,
                align: 'right',
                minWidth: '70px',
                formatter: (v) => v != null ? `${v.toFixed(0)}%` : '-'
              });
            }
            setDynamicColumns(dayCols);
          }
          break;
        case 'occupancy-yearly':
          response = await reportsService.getYearlyOccupancy({ year: occupancyYear });
          break;
        case 'payment-report': {
          // Build params specifically for payment report (payments made in the period;
          // booking filters apply to each payment's booking)
          const paymentParams = { ...periodQuery(period) };
          if (paymentFilterStatus) paymentParams.paymentStatus = paymentFilterStatus;
          if (filters.status) paymentParams.bookingStatus = filters.status;
          if (filters.bookingPlatform) paymentParams.bookingPlatform = filters.bookingPlatform;
          if (filterCreatedAtStart) paymentParams.createdAtStart = filterCreatedAtStart;
          if (filterCreatedAtEnd) paymentParams.createdAtEnd = filterCreatedAtEnd;
          paymentParams.limit = 0;
          
          response = await reportsService.getPayments(paymentParams);
          
          if (response.success && response.data) {
            // Store original data for client-side filtering
            setPaymentOriginalData(response.data);
          }
          break;
        }
        case 'accounts-master': {
          // Build params for accounts master report - send filters directly to backend.
          // compact: only add-on column groups some booking in these months used.
          const acctMasterParams = { ...periodQuery(period), compact: 1 };
          if (filterCreatedAtStart) acctMasterParams.createdAtStart = filterCreatedAtStart;
          if (filterCreatedAtEnd) acctMasterParams.createdAtEnd = filterCreatedAtEnd;
          if (acctMasterFilterStatus) acctMasterParams.status = acctMasterFilterStatus;
          if (acctMasterFilterPaymentStatus) acctMasterParams.paymentStatus = acctMasterFilterPaymentStatus;
          if (acctMasterFilterPlatform) acctMasterParams.bookingPlatform = acctMasterFilterPlatform;
          acctMasterParams.order = 'room';
          acctMasterParams.limit = 1000;
          
          // Fetch all pages
          console.log('[accounts-master] Fetching page 1...');
          const firstPage = await reportsService.getAccountsMaster({ ...acctMasterParams, page: 1 });
          
          if (!firstPage.success) {
            throw new Error(firstPage.message || 'Failed to fetch accounts master data');
          }
          
          allData = firstPage.data || [];
          
          if (firstPage.pagination) {
            const { totalPages, total } = firstPage.pagination;
            console.log(`[accounts-master] Total pages: ${totalPages}, Total records: ${total}`);
            setLoadingProgress({ current: 1, total: totalPages });
            
            if (totalPages > 1) {
              const pagePromises = [];
              for (let page = 2; page <= totalPages; page++) {
                pagePromises.push(
                  reportsService.getAccountsMaster({ ...acctMasterParams, page, limit: 1000 }).then(res => {
                    setLoadingProgress({ current: page, total: totalPages });
                    return res.data || [];
                  })
                );
              }
              const remainingPages = await Promise.all(pagePromises);
              remainingPages.forEach(pageData => {
                allData = [...allData, ...pageData];
              });
            }
          }
          
          response = { success: true, data: allData };
          
          // Extract dynamic addon columns from the data
          if (allData.length > 0) {
            const addonColKeys = new Set();
            allData.forEach(row => {
              Object.keys(row).forEach(key => {
                if (key.startsWith('addon_')) {
                  addonColKeys.add(key);
                }
              });
            });
            
            // Group addon columns by service name prefix
            const serviceGroups = {};
            addonColKeys.forEach(key => {
              // Extract service prefix: addon_{ServiceName}_{field}
              const parts = key.split('_');
              // Find the field suffix (last part)
              const fieldSuffixes = ['serviceName', 'totalPrice', 'totalFinalAmount', 'quantity', 'items', 'tax', 'discount', 'fee', 'priceBreakdowns'];
              let matchedSuffix = null;
              for (const suffix of fieldSuffixes) {
                if (key.endsWith('_' + suffix)) {
                  matchedSuffix = suffix;
                  break;
                }
              }
              if (matchedSuffix) {
                const prefix = key.substring(0, key.length - matchedSuffix.length - 1);
                if (!serviceGroups[prefix]) serviceGroups[prefix] = [];
                serviceGroups[prefix].push({ key, suffix: matchedSuffix });
              }
            });
            
            // Create ordered dynamic columns for each addon service
            const addonDynCols = [];
            const suffixOrder = ['serviceName', 'totalPrice', 'totalFinalAmount', 'quantity', 'items', 'tax', 'discount', 'fee'];
            const suffixLabels = {
              serviceName: 'Service',
              totalPrice: 'Price',
              totalFinalAmount: 'Final Amt',
              quantity: 'Qty',
              items: 'Items',
              tax: 'Tax',
              discount: 'Discount',
              fee: 'Fee'
            };
            
            Object.keys(serviceGroups).sort().forEach(prefix => {
              const group = serviceGroups[prefix];
              // Get display name from serviceName column
              const serviceNameKey = `${prefix}_serviceName`;
              const displayName = allData.find(r => r[serviceNameKey])?.[ serviceNameKey] || prefix.replace('addon_', '').replace(/_/g, ' ');
              
              suffixOrder.forEach(suffix => {
                const col = group.find(g => g.suffix === suffix);
                if (col) {
                  const isCurrency = ['totalPrice', 'totalFinalAmount', 'tax', 'discount', 'fee'].includes(suffix);
                  addonDynCols.push({
                    key: col.key,
                    label: `${displayName} - ${suffixLabels[suffix]}`,
                    type: isCurrency ? 'currency' : undefined,
                    align: isCurrency || suffix === 'quantity' ? 'right' : undefined,
                    minWidth: suffix === 'items' ? '180px' : '110px'
                  });
                }
              });
            });
            
            setDynamicColumns(addonDynCols);
          }
          break;
        }
        default:
          toast.error('Invalid report type');
          setLoading(false);
          return;
      }

      if (response.success) {
        let processedData = response.data || [];
        let additionalCols = [];
        
        // Apply client-side date overlap filtering (non-month reports with a date range)
        // Show all bookings whose check-in/check-out period overlaps with filter date range
        if (!monthScoped && (filters.startDate || filters.endDate)) {
          const filterStart = filters.startDate ? new Date(filters.startDate) : null;
          const filterEnd = filters.endDate ? new Date(filters.endDate) : null;
          if (filterStart) filterStart.setHours(0, 0, 0, 0);
          if (filterEnd) filterEnd.setHours(23, 59, 59, 999);

          const beforeCount = processedData.length;
          processedData = processedData.filter(row => {
            const checkIn = row.checkInDate ? new Date(row.checkInDate) : null;
            const checkOut = row.checkOutDate ? new Date(row.checkOutDate) : null;
            // Fallback to bookingDate for reports like accounts
            const bookingDate = row.bookingDate ? new Date(row.bookingDate) : null;

            // If record has no dates at all, keep it
            if (!checkIn && !checkOut && !bookingDate) return true;

            // Overlap logic: booking overlaps filter range if
            // bookingStart <= filterEnd AND bookingEnd >= filterStart
            const bookingStart = checkIn || bookingDate || checkOut;
            const bookingEnd = checkOut || bookingDate || checkIn;

            if (filterStart && filterEnd) {
              return bookingStart <= filterEnd && bookingEnd >= filterStart;
            } else if (filterStart) {
              return bookingEnd >= filterStart;
            } else if (filterEnd) {
              return bookingStart <= filterEnd;
            }
            return true;
          });
          console.log(`[${activeTab}] Date overlap filter: ${beforeCount} -> ${processedData.length} records`);
        }
        
        // Store raw data for master-booking before any processing
        if (activeTab === 'master-booking') {
          setRawData(processedData);
        }
        
        // Extract unique options for master-booking filters
        if (activeTab === 'master-booking' && processedData.length > 0) {
          const courses = extractUniqueOptions(processedData, 'courses', 'courseName');
          const roomTypes = extractUniqueOptions(processedData, 'rooms', 'roomTypeName');
          const bedTypes = extractUniqueOptions(processedData, 'beds', 'bedTypeName');
          
          setCourseOptions(courses);
          setRoomTypeOptions(roomTypes);
          setBedTypeOptions(bedTypes);
          
        }
        
        // Flatten or merge nested arrays based on report type and view mode
        if (processedData.length > 0) {
          let arrayKeys = [];
          
          switch (activeTab) {
            case 'master-booking':
              arrayKeys = ['rooms', 'beds', 'courses', 'addOns', 'payments', 'refunds'];
              break;
            case 'occupancy':
              // No array flattening needed - occupancy data is already flat
              arrayKeys = [];
              break;
            case 'accounts':
              arrayKeys = ['allTaxes'];
              break;
            case 'check-in':
              arrayKeys = ['rooms'];
              break;
            case 'check-out':
              arrayKeys = ['rooms', 'addOns'];
              break;
            // Add more cases as needed for other report types
            default:
              arrayKeys = [];
          }
          
          console.log(`[${activeTab}] Array keys to process:`, arrayKeys);
          console.log(`[${activeTab}] View mode:`, viewMode);
          
          if (arrayKeys.length > 0) {
            if (viewMode === 'accounts' && activeTab === 'master-booking') {
              // Use accounts view (financial/tax focused)
              const { accountsData, additionalColumns } = createAccountsView(processedData, allAddonServices);
              processedData = accountsData;
              additionalCols = additionalColumns;
              
              console.log(`[${activeTab}] Accounts view data:`, processedData.length, 'records');
              console.log(`[${activeTab}] Dynamic columns created:`, additionalCols.length);
            } else if (viewMode === 'merged' && activeTab === 'master-booking') {
              // Use merged view (comma-separated)
              const { mergedData, additionalColumns } = mergeNestedArrays(processedData, arrayKeys);
              processedData = mergedData;
              additionalCols = additionalColumns;
              
              console.log(`[${activeTab}] Merged data:`, processedData.length, 'records');
              console.log(`[${activeTab}] Dynamic columns created:`, additionalCols.length);
            } else {
              // Use expanded view (separate columns)
              const { flattenedData, additionalColumns } = flattenNestedArrays(processedData, arrayKeys);
              processedData = flattenedData;
              additionalCols = additionalColumns;
              
              console.log(`[${activeTab}] Flattened data:`, processedData.length, 'records');
              console.log(`[${activeTab}] Dynamic columns created:`, additionalCols.length);
            }
          }
        }
        
        // Store original data for master-booking (before filtering)
        if (activeTab === 'master-booking') {
          setOriginalData(processedData);
          console.log(`[${activeTab}] Stored original data:`, processedData.length, 'records');
          
          // Apply filters if any are active
          const filteredData = applyFilters(processedData);
          console.log(`[${activeTab}] After filters:`, filteredData.length, 'records');
          processedData = filteredData;
        }
        
        // Extract unique course names and store original data for course-participants and all-guests
        if (activeTab === 'course-participants' || activeTab === 'all-guests') {
          if (processedData.length > 0) {
            const uniqueCourses = [...new Set(
              processedData.map(row => row.course).filter(Boolean)
            )].sort();
            setAllCourses(uniqueCourses);
          }
          setOriginalData(processedData);

          // Apply client-side course name filter
          if (filterCourseParticipant) {
            const beforeCount = processedData.length;
            processedData = processedData.filter(row => row.course === filterCourseParticipant);
            console.log(`[${activeTab}] Course filter: ${beforeCount} -> ${processedData.length} records`);
          }
        }
        
        // Apply client-side payment report filters
        if (activeTab === 'payment-report') {
          processedData = applyPaymentFilters(processedData);
        }
        
        setReportData(processedData);
        // Only set dynamicColumns if not already set by occupancy-monthly or accounts-master (which set them before)
        if (activeTab !== 'occupancy-monthly' && activeTab !== 'accounts-master') {
          setDynamicColumns(additionalCols);
        }
        setReportSummary(response.summary || null);
        toast.success(`${reportTabs.find(t => t.id === activeTab)?.label} loaded successfully`);
      } else {
        toast.error(response.message || 'Failed to load report');
        setReportData([]);
        setOriginalData([]);
        setRawData([]);
        setDynamicColumns([]);
        setReportSummary(null);
      }
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to load report: ' + (error.message || 'Unknown error'));
      setReportData([]);
      setOriginalData([]);
      setRawData([]);
      setDynamicColumns([]);
      setReportSummary(null);
    } finally {
      setLoading(false);
    }
  };

  // Process raw data based on view mode (for master-booking)
  const processDataByViewMode = (data) => {
    if (!data || data.length === 0) return { processedData: [], additionalCols: [] };
    
    const arrayKeys = ['rooms', 'beds', 'courses', 'addOns', 'payments', 'refunds'];
    let processedData = data;
    let additionalCols = [];
    
    if (viewMode === 'accounts') {
      const { accountsData, additionalColumns } = createAccountsView(data, allAddonServices);
      processedData = accountsData;
      additionalCols = additionalColumns;
    } else if (viewMode === 'merged') {
      const { mergedData, additionalColumns } = mergeNestedArrays(data, arrayKeys);
      processedData = mergedData;
      additionalCols = additionalColumns;
    } else {
      const { flattenedData, additionalColumns } = flattenNestedArrays(data, arrayKeys);
      processedData = flattenedData;
      additionalCols = additionalColumns;
    }
    
    return { processedData, additionalCols };
  };

  // Fetch all courses from backend on mount
  useEffect(() => {
    // Reset course filter when switching tabs
    setFilterCourseParticipant('');
  }, [activeTab]);

  // Re-filter course-participants / all-guests when course dropdown changes
  useEffect(() => {
    if ((activeTab === 'course-participants' || activeTab === 'all-guests') && originalData.length > 0) {
      if (filterCourseParticipant) {
        const filtered = originalData.filter(row => row.course === filterCourseParticipant);
        setReportData(filtered);
      } else {
        setReportData(originalData);
      }
    }
  }, [filterCourseParticipant]);

  // Re-apply payment report client-side filters when filter state changes
  useEffect(() => {
    if (activeTab === 'payment-report' && paymentOriginalData.length > 0) {
      const filtered = applyPaymentFilters(paymentOriginalData);
      setReportData(filtered);
    }
  }, [paymentFilterCheckInStart, paymentFilterCheckInEnd, paymentFilterAmountMin, paymentFilterAmountMax, paymentFilterGuestName, paymentFilterAmountSearch, paymentFilterMethod]);

  // Re-fetch payment report when its server-side status filter changes
  useEffect(() => {
    if (activeTab === 'payment-report' && appliedPeriod) {
      fetchReport();
    }
  }, [paymentFilterStatus]);

  // Re-fetch occupancy reports when year/month changes
  useEffect(() => {
    if (activeTab === 'occupancy-monthly' || activeTab === 'occupancy-yearly') {
      fetchReport();
    }
  }, [occupancyYear, occupancyMonth]);

  // Load report on tab change. Month-scoped reports load nothing until months have
  // been chosen with "Load report"; after that, switching tabs loads the same months.
  useEffect(() => {
    // Reset view mode when changing tabs
    setViewMode('expanded');
    fetchReport();
  }, [activeTab]);

  const loadSelectedPeriod = () => {
    let period;
    if (periodMode === 'range') {
      if (!rangeFrom || !rangeTo) {
        toast.warning('Choose both a start and an end date');
        return;
      }
      if (rangeTo < rangeFrom) {
        toast.warning('The end date is before the start date');
        return;
      }
      const days = (Date.parse(rangeTo) - Date.parse(rangeFrom)) / 86400000 + 1;
      if (days > MAX_RANGE_DAYS) {
        toast.warning('Choose a range of at most two years');
        return;
      }
      period = { mode: 'range', from: rangeFrom, to: rangeTo };
    } else {
      if (selectedMonths.length === 0) {
        toast.warning('Tick at least one month first');
        return;
      }
      period = { mode: 'months', months: [...selectedMonths].sort() };
    }
    setAppliedPeriod(period);
    fetchReport(period);
  };

  const toggleMonth = (key) => {
    setSelectedMonths(prev => {
      if (prev.includes(key)) return prev.filter(k => k !== key);
      if (prev.length >= MAX_SELECTED_MONTHS) {
        toast.warning(`Select up to ${MAX_SELECTED_MONTHS} months at a time`);
        return prev;
      }
      return [...prev, key];
    });
  };

  // Quick picks: the last `count` months ending this month
  const pickRecentMonths = (count) => {
    const keys = [];
    for (let i = count - 1; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      keys.push(monthKey(d.getFullYear(), d.getMonth() + 1));
    }
    setSelectedMonths(keys);
    setPeriodYear(today.getFullYear());
  };

  const draftPeriod = periodMode === 'range'
    ? { mode: 'range', from: rangeFrom, to: rangeTo }
    : { mode: 'months', months: [...selectedMonths].sort() };
  const selectionChanged = isMonthScoped && !!appliedPeriod && periodKey(draftPeriod) !== periodKey(appliedPeriod);
  const canLoadPeriod = periodMode === 'range' ? Boolean(rangeFrom && rangeTo) : selectedMonths.length > 0;

  // Booking filters (status, platform, created dates) are applied by the server, so a
  // change reloads the loaded period. Master Booking filters status/platform itself.
  useEffect(() => {
    if (!isMonthScoped || !appliedPeriod) return;
    fetchReport();
  }, [filterCreatedAtStart, filterCreatedAtEnd]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isMonthScoped || !appliedPeriod || activeTab === 'master-booking') return;
    fetchReport();
  }, [filters.status, filters.bookingPlatform]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle view mode change - reprocess raw data
  useEffect(() => {
    if (activeTab === 'master-booking' && rawData.length > 0) {
      console.log('[master-booking] View mode changed to:', viewMode);
      console.log('[master-booking] Reprocessing raw data...');
      
      const { processedData, additionalCols } = processDataByViewMode(rawData);
      
      console.log(`[${activeTab}] Processed data:`, processedData.length, 'records');
      console.log(`[${activeTab}] Dynamic columns:`, additionalCols.length);
      
      // Store as original data (before filtering)
      setOriginalData(processedData);
      setDynamicColumns(additionalCols);
      
      // Apply filters
      const filteredData = applyFilters(processedData);
      console.log(`[${activeTab}] After filters:`, filteredData.length, 'records');
      
      setReportData(filteredData);
      
      toast.success(`View mode changed to ${viewMode}`);
    }
  }, [viewMode]);

  // Re-apply filters when master-booking filters change (client-side filtering)
  useEffect(() => {
    if (activeTab === 'master-booking' && originalData.length > 0) {
      console.log('[master-booking] Filters or sort sequence changed, re-applying from original data...');
      
      // Apply filters to original data
      const filteredData = applyFilters(originalData);
      console.log(`[${activeTab}] After filters:`, filteredData.length, 'of', originalData.length, 'records');
      
      setReportData(filteredData);
    }
  }, [filterCourseName, filterRoomType, filterBedType, filterPaymentStatus, 
      filterBookingStatus, filterBookingPlatform, filterOmgExperience, filterGender,
      filterPaymentDateStart, filterPaymentDateEnd]);

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Helper function to format dates to IST for CSV export
  const formatValueForCSV = (value, column) => {
    if (value === null || value === undefined) return '';
    
    // Handle objects
    if (typeof value === 'object') {
      return JSON.stringify(value).replace(/"/g, '""');
    }
    
    // Handle date/datetime columns - convert to IST
    if (column.type === 'date' || column.type === 'datetime') {
      try {
        const date = new Date(value);
        if (isNaN(date.getTime())) {
          return String(value).replace(/"/g, '""');
        }
        
        // Format options for IST (Asia/Kolkata)
        const options = {
          timeZone: 'Asia/Kolkata',
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          ...(column.type === 'datetime' && {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          })
        };
        
        const formatter = new Intl.DateTimeFormat('en-IN', options);
        return formatter.format(date);
      } catch (error) {
        return String(value).replace(/"/g, '""');
      }
    }
    
    // Auto-detect date strings and convert to IST
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          const options = {
            timeZone: 'Asia/Kolkata',
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          };
          const formatter = new Intl.DateTimeFormat('en-IN', options);
          return formatter.format(date);
        }
      } catch (error) {
        // Fall through to default handling
      }
    }
    
    // Handle regular values
    return String(value).replace(/"/g, '""');
  };

  // Direct download CSV from backend
  const handleDirectDownload = async () => {
    setDownloading(true);
    try {
      const params = { ...filters };
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      if (activeTab !== 'daily-summary') {
        params.order = 'room';
      }
      if (isMonthScoped) {
        Object.assign(params, periodQuery(appliedPeriod));
        delete params.startDate;
        delete params.endDate;
      }

      // Add accounts-master specific filters
      if (activeTab === 'accounts-master') {
        if (acctMasterFilterStatus) params.status = acctMasterFilterStatus;
        if (acctMasterFilterPaymentStatus) params.paymentStatus = acctMasterFilterPaymentStatus;
        if (acctMasterFilterPlatform) params.bookingPlatform = acctMasterFilterPlatform;
      }

      const queryString = new URLSearchParams(params).toString();
      const endpoint = `${API_CONFIG.BASE_URL}/api/reports/download/${activeTab}${queryString ? `?${queryString}` : ''}`;

      const token = localStorage.getItem('token');
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `${activeTab}-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);

      toast.success('Report downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.message || 'Failed to download report');
    } finally {
      setDownloading(false);
    }
  };

  // Export to CSV
  const handleExport = () => {
    if (!reportData || reportData.length === 0) {
      toast.warning('No data to export');
      return;
    }

    // Get all columns including dynamic ones
    const baseColumns = viewMode === 'accounts' && activeTab === 'master-booking'
      ? [
          { key: 'userName', label: 'Guest Name', minWidth: '150px' },
          { key: 'checkInDate', label: 'Check-in', type: 'date', minWidth: '110px' },
          { key: 'checkOutDate', label: 'Check-out', type: 'date', minWidth: '110px' },
          { key: 'finalAmount', label: 'Booking Total', type: 'currency', align: 'right', minWidth: '120px' },
        ]
      : getColumnsForReport(activeTab);
    const allColumns = [...baseColumns, ...dynamicColumns];
    const reportName = reportTabs.find(t => t.id === activeTab)?.label || 'Report';

    // Create CSV headers
    const headers = allColumns.map(col => col.label);
    
    // Create CSV rows - format dates to IST
    const rows = reportData.map(row => {
      return allColumns.map(col => {
        const value = row[col.key];
        return formatValueForCSV(value, col);
      });
    });

    // Combine into CSV
    const csvContent = [
      headers.map(h => `"${h}"`).join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Download
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${reportName.replace(/\s+/g, '-')}-${format(new Date(), 'yyyy-MM-dd-HHmm')}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);

    toast.success(`Exported ${reportData.length} records to CSV`);
  };

  // Render filter controls based on active tab
  const renderFilters = () => {
    const commonDateFilters = (
      <>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Start Date</label>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #D2CDBF',
              borderRadius: '6px',
              fontSize: '14px',
              outline: 'none'
            }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>End Date</label>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #D2CDBF',
              borderRadius: '6px',
              fontSize: '14px',
              outline: 'none'
            }}
          />
        </div>
      </>
    );

    // The same booking filters on every booking report (applied by the server)
    const selectStyle = { padding: '8px 12px', border: '1px solid #D2CDBF', borderRadius: '6px', fontSize: '14px', outline: 'none', cursor: 'pointer' };
    const dateStyle = { padding: '8px 12px', border: '1px solid #D2CDBF', borderRadius: '6px', fontSize: '14px', outline: 'none' };
    const labelStyle = { fontSize: '12px', fontWeight: '600', color: '#36433B' };
    const bookingFilterControls = (
      <>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={labelStyle}>Booking Status</label>
          <select value={filters.status || ''} onChange={(e) => handleFilterChange('status', e.target.value)} style={selectStyle}>
            <option value="">All Booking Statuses</option>
            {BOOKING_STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={labelStyle}>Booking Platform</label>
          <select value={filters.bookingPlatform || ''} onChange={(e) => handleFilterChange('bookingPlatform', e.target.value)} style={selectStyle}>
            <option value="">All Platforms</option>
            {BOOKING_PLATFORM_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={labelStyle}>Booking Created From</label>
          <input type="date" value={filterCreatedAtStart} onChange={(e) => setFilterCreatedAtStart(e.target.value)} style={dateStyle} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={labelStyle}>Booking Created To</label>
          <input type="date" value={filterCreatedAtEnd} onChange={(e) => setFilterCreatedAtEnd(e.target.value)} style={dateStyle} />
        </div>
      </>
    );

    if (activeTab === 'daily-summary') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Report Date</label>
          <input
            type="date"
            value={filters.date}
            onChange={(e) => handleFilterChange('date', e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #D2CDBF',
              borderRadius: '6px',
              fontSize: '14px',
              outline: 'none'
            }}
          />
        </div>
      );
    }

    if (['master-booking', 'check-in', 'check-out', 'accounts'].includes(activeTab)) {
      // Master booking gets advanced filters
      if (activeTab === 'master-booking') {
        return (
          <>
            {/* Course Name Filter */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Course Name</label>
              <select
                value={filterCourseName}
                onChange={(e) => setFilterCourseName(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #D2CDBF',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="">All Courses</option>
                {courseOptions.map(course => (
                  <option key={course} value={course}>{course}</option>
                ))}
              </select>
            </div>

            {/* Room Type Filter */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Room Type</label>
              <select
                value={filterRoomType}
                onChange={(e) => setFilterRoomType(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #D2CDBF',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="">All Room Types</option>
                {roomTypeOptions.map(roomType => (
                  <option key={roomType} value={roomType}>{roomType}</option>
                ))}
              </select>
            </div>

            {/* Bed Type Filter */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Bed Type</label>
              <select
                value={filterBedType}
                onChange={(e) => setFilterBedType(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #D2CDBF',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="">All Bed Types</option>
                {bedTypeOptions.map(bedType => (
                  <option key={bedType} value={bedType}>{bedType}</option>
                ))}
              </select>
            </div>

            {/* Payment Status Filter */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Payment Status</label>
              <select
                value={filterPaymentStatus}
                onChange={(e) => setFilterPaymentStatus(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #D2CDBF',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="">All Payment Statuses</option>
                <option value="PAID">Paid</option>
                <option value="PARTIAL">Partial</option>
                <option value="PENDING">Pending</option>
              </select>
            </div>

            {/* Booking Status Filter */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Booking Status</label>
              <select
                value={filterBookingStatus}
                onChange={(e) => setFilterBookingStatus(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #D2CDBF',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="">All Booking Statuses</option>
                <option value="PENDING">Pending</option>
                <option value="CONFIRMED">Confirmed</option>
                <option value="CHECKEDIN">Checked In</option>
                <option value="CHECKEDOUT">Checked Out</option>
                <option value="CANCELLED">Cancelled</option>
                <option value="COMPLETED">Completed</option>
              </select>
            </div>

            {/* Booking Platform Filter */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Booking Platform</label>
              <select
                value={filterBookingPlatform}
                onChange={(e) => setFilterBookingPlatform(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #D2CDBF',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="">All Platforms</option>
                {BOOKING_PLATFORM_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>

            {/* OMG Experience Filter */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>OMG Experience</label>
              <select
                value={filterOmgExperience}
                onChange={(e) => setFilterOmgExperience(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #D2CDBF',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="">All</option>
                <option value="YES">Yes (no course)</option>
                <option value="NO">No (with a course)</option>
              </select>
            </div>

            {/* Gender Filter */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Gender</label>
              <select
                value={filterGender}
                onChange={(e) => setFilterGender(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #D2CDBF',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="">All Genders</option>
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
                <option value="OTHER">Other</option>
              </select>
            </div>

            {/* Payment Date Range Filter */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Payment Date From</label>
              <input
                type="date"
                value={filterPaymentDateStart}
                onChange={(e) => setFilterPaymentDateStart(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #D2CDBF',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Payment Date To</label>
              <input
                type="date"
                value={filterPaymentDateEnd}
                onChange={(e) => setFilterPaymentDateEnd(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #D2CDBF',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>

            {/* Booking Created At Date Range Filter */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Booking Created From</label>
              <input
                type="date"
                value={filterCreatedAtStart}
                onChange={(e) => setFilterCreatedAtStart(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #D2CDBF',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Booking Created To</label>
              <input
                type="date"
                value={filterCreatedAtEnd}
                onChange={(e) => setFilterCreatedAtEnd(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #D2CDBF',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>
          </>
        );
      }
      
      // Accounts & GST: the shared booking filters (period comes from the selector)
      if (activeTab === 'accounts') return bookingFilterControls;

      // Check-in / check-out keep their day range (default today) and status
      return (
        <>
          {commonDateFilters}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="CONFIRMED">Confirmed</option>
              <option value="CHECKEDIN">Checked In</option>
              <option value="CHECKEDOUT">Checked Out</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="COMPLETED">Completed</option>
            </select>
          </div>
        </>
      );
    }

    if (activeTab === 'occupancy') {
      return (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Property</label>
            <input
              type="text"
              placeholder="Filter by property..."
              value={filters.propertyId || ''}
              onChange={(e) => handleFilterChange('propertyId', e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none'
              }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Room Type</label>
            <input
              type="text"
              placeholder="Filter by room type..."
              value={filters.roomTypeId || ''}
              onChange={(e) => handleFilterChange('roomTypeId', e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none'
              }}
            />
          </div>
        </>
      );
    }

    if (activeTab === 'course-participants' || activeTab === 'all-guests') {
      return (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Course Name</label>
            <select
              value={filterCourseParticipant}
              onChange={(e) => setFilterCourseParticipant(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="">All Courses</option>
              {allCourses.map(courseName => (
                <option key={courseName} value={courseName}>
                  {courseName}
                </option>
              ))}
            </select>
          </div>
          {bookingFilterControls}
        </>
      );
    }

    if (activeTab === 'omg-experience') {
      return (
        <>
          {bookingFilterControls}
        </>
      );
    }

    if (activeTab === 'services-inventory') {
      return commonDateFilters;
    }

    if (activeTab === 'accounts-master') {
      return (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Booking Status</label>
            <select
              value={acctMasterFilterStatus}
              onChange={(e) => setAcctMasterFilterStatus(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="CONFIRMED">Confirmed</option>
              <option value="CHECKEDIN">Checked In</option>
              <option value="CHECKEDOUT">Checked Out</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="COMPLETED">Completed</option>
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Payment Status</label>
            <select
              value={acctMasterFilterPaymentStatus}
              onChange={(e) => setAcctMasterFilterPaymentStatus(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="">All Payment Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="SUCCESS">Success</option>
              <option value="FAILED">Failed</option>
              <option value="REFUNDED">Refunded</option>
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Booking Platform</label>
            <select
              value={acctMasterFilterPlatform}
              onChange={(e) => setAcctMasterFilterPlatform(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="">All Platforms</option>
              <option value="BOOKING_ENGINE">Booking Engine</option>
              <option value="STAFF_RESERVATION">Staff Reservation</option>
              <option value="OTHERS">Others</option>
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={labelStyle}>Booking Created From</label>
            <input type="date" value={filterCreatedAtStart} onChange={(e) => setFilterCreatedAtStart(e.target.value)} style={dateStyle} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={labelStyle}>Booking Created To</label>
            <input type="date" value={filterCreatedAtEnd} onChange={(e) => setFilterCreatedAtEnd(e.target.value)} style={dateStyle} />
          </div>
        </>
      );
    }

    if (activeTab === 'occupancy-monthly') {
      return (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Year</label>
            <input
              type="number"
              value={occupancyYear}
              onChange={(e) => setOccupancyYear(parseInt(e.target.value) || new Date().getFullYear())}
              min="2020"
              max="2099"
              style={{
                padding: '8px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none',
                width: '100px'
              }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Month</label>
            <select
              value={occupancyMonth}
              onChange={(e) => setOccupancyMonth(parseInt(e.target.value))}
              style={{
                padding: '8px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value={1}>January</option>
              <option value={2}>February</option>
              <option value={3}>March</option>
              <option value={4}>April</option>
              <option value={5}>May</option>
              <option value={6}>June</option>
              <option value={7}>July</option>
              <option value={8}>August</option>
              <option value={9}>September</option>
              <option value={10}>October</option>
              <option value={11}>November</option>
              <option value={12}>December</option>
            </select>
          </div>
        </>
      );
    }

    if (activeTab === 'occupancy-yearly') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Year</label>
          <input
            type="number"
            value={occupancyYear}
            onChange={(e) => setOccupancyYear(parseInt(e.target.value) || new Date().getFullYear())}
            min="2020"
            max="2099"
            style={{
              padding: '8px 12px',
              border: '1px solid #D2CDBF',
              borderRadius: '6px',
              fontSize: '14px',
              outline: 'none',
              width: '100px'
            }}
          />
        </div>
      );
    }

    if (activeTab === 'payment-report') {
      return (
        <>
          {/* Check-in/Check-out Date Range */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Check-in From</label>
            <input
              type="date"
              value={paymentFilterCheckInStart}
              onChange={(e) => setPaymentFilterCheckInStart(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none'
              }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Check-in To</label>
            <input
              type="date"
              value={paymentFilterCheckInEnd}
              onChange={(e) => setPaymentFilterCheckInEnd(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none'
              }}
            />
          </div>

          {/* Amount Range */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Min Amount</label>
            <input
              type="number"
              value={paymentFilterAmountMin}
              onChange={(e) => setPaymentFilterAmountMin(e.target.value)}
              placeholder="Min"
              style={{
                padding: '8px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none',
                width: '100px'
              }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Max Amount</label>
            <input
              type="number"
              value={paymentFilterAmountMax}
              onChange={(e) => setPaymentFilterAmountMax(e.target.value)}
              placeholder="Max"
              style={{
                padding: '8px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none',
                width: '100px'
              }}
            />
          </div>

          {/* Guest Name Search */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Guest Name</label>
            <input
              type="text"
              value={paymentFilterGuestName}
              onChange={(e) => setPaymentFilterGuestName(e.target.value)}
              placeholder="Search guest..."
              style={{
                padding: '8px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none'
              }}
            />
          </div>

          {/* Exact Amount Search */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Amount Search</label>
            <input
              type="number"
              value={paymentFilterAmountSearch}
              onChange={(e) => setPaymentFilterAmountSearch(e.target.value)}
              placeholder="Exact amount"
              style={{
                padding: '8px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none',
                width: '120px'
              }}
            />
          </div>

          {/* Payment Status */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Payment Status</label>
            <select
              value={paymentFilterStatus}
              onChange={(e) => setPaymentFilterStatus(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="SUCCESS">Success</option>
              <option value="FAILED">Failed</option>
              <option value="REFUNDED">Refunded</option>
            </select>
          </div>

          {/* Payment Method */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>Payment Method</label>
            <select
              value={paymentFilterMethod}
              onChange={(e) => setPaymentFilterMethod(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="">All Methods</option>
              <option value="CASH">Cash</option>
              <option value="UPI">UPI</option>
              <option value="CARD">Card</option>
              <option value="BANK_TRANSFER">Bank Transfer</option>
              <option value="RAZORPAY">Razorpay</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
          {bookingFilterControls}
        </>
      );
    }

    return null;
  };

  // Dynamic columns (expanded rooms/beds/courses, add-on groups) that are empty for
  // every loaded row are hidden on screen; the CSV export still has all of them.
  const visibleDynamicColumns = useMemo(() => {
    if (!hideEmptyColumns || dynamicColumns.length === 0) return dynamicColumns;
    return dynamicColumns.filter(col => reportData.some(row => isMeaningful(row[col.key])));
  }, [dynamicColumns, reportData, hideEmptyColumns]);
  const hiddenColumnCount = dynamicColumns.length - visibleDynamicColumns.length;

  // Render summary section
  const renderSummary = () => {
    const guestList = GUEST_LIST_TABS.includes(activeTab);
    const items = guestList
      ? (reportData.length ? buildGuestSummary(activeTab, reportData) : [])
      : serverSummaryItems(reportSummary);
    if (items.length === 0) return null;

    return (
      <div style={{
        backgroundColor: '#F5F3EB',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px',
        border: '1px solid #E3DFD3'
      }}>
        <div style={{ fontWeight: '600', marginBottom: '16px', fontSize: '16px', color: '#36433B' }}>
          Report Summary
          {guestList && (
            <span style={{ fontWeight: '400', fontSize: '12px', color: '#5F6B61', marginLeft: '8px' }}>
              for the {reportData.length} rows shown, each booking counted once
            </span>
          )}
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
          gap: '16px'
        }}>
          {items.map(({ label, value, currency }) => (
            <div key={label} style={{
              backgroundColor: 'white',
              padding: '12px 16px',
              borderRadius: '6px',
              border: '1px solid #E3DFD3'
            }}>
              <div style={{ fontSize: '12px', color: '#5F6B61', marginBottom: '4px' }}>{label}</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#1A3E2A' }}>
                {currency ? formatRupees(value) : typeof value === 'number' ? value.toLocaleString('en-IN') : String(value ?? '-')}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: isMobile ? '12px' : '24px', backgroundColor: '#F5F3EB', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        padding: isMobile ? '16px' : '24px',
        borderRadius: '12px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h1 style={{ margin: '0 0 8px 0', fontSize: isMobile ? '22px' : '28px', fontWeight: '700', color: '#1A3E2A' }}>
          Comprehensive Reports
        </h1>
        <p style={{ margin: 0, fontSize: '14px', color: '#5F6B61' }}>
          Professional reporting system with multiple report types and export capabilities
        </p>
      </div>

      {/* Sub-tabs Navigation */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <div style={{
          display: 'flex',
          overflowX: 'auto',
          borderBottom: '2px solid #E3DFD3'
        }}>
          {reportTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                if (tab.id === activeTab) return;
                // drop the previous report at once so its rows never show under the new columns
                clearReport();
                setLoading(!MONTH_SCOPED_TABS.includes(tab.id) || !!appliedPeriod);
                setActiveTab(tab.id);
              }}
              style={{
                flex: '0 0 auto',
                padding: '16px 24px',
                backgroundColor: activeTab === tab.id ? '#1A3E2A' : 'transparent',
                color: activeTab === tab.id ? 'white' : '#5F6B61',
                border: 'none',
                borderBottom: activeTab === tab.id ? '3px solid #163626' : '3px solid transparent',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.backgroundColor = '#EEEBE1';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
        
        {/* Tab description */}
        <div style={{ padding: isMobile ? '10px 12px' : '12px 24px', backgroundColor: '#F5F3EB', fontSize: '13px', color: '#5F6B61' }}>
          {reportTabs.find(t => t.id === activeTab)?.description}
        </div>
      </div>

      {/* Period selector: month-scoped reports load only the chosen months or date range */}
      {isMonthScoped && (
        <div style={{
          backgroundColor: 'white',
          padding: isMobile ? '12px' : '20px',
          borderRadius: '12px',
          marginBottom: '16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: selectionChanged ? '1px solid #A9613C' : '1px solid transparent'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{ fontSize: '15px', fontWeight: '700', color: '#1A3E2A' }}>Period</div>
                <div role="tablist" style={{ display: 'inline-flex', border: '1px solid #D2CDBF', borderRadius: '8px', overflow: 'hidden' }}>
                  {[['months', 'Months'], ['range', 'Date range']].map(([mode, label]) => (
                    <button
                      key={mode}
                      type="button"
                      role="tab"
                      aria-selected={periodMode === mode}
                      onClick={() => setPeriodMode(mode)}
                      style={{
                        padding: '5px 12px',
                        border: 'none',
                        backgroundColor: periodMode === mode ? '#1A3E2A' : '#FBFAF6',
                        color: periodMode === mode ? 'white' : '#36433B',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ fontSize: '12px', color: '#5F6B61', marginTop: '6px' }}>
                {activeTab === 'payment-report'
                  ? `Payments made in the ${periodMode === 'range' ? 'dates' : 'ticked months'}`
                  : `Bookings whose stay (check-in to check-out) overlaps the ${periodMode === 'range' ? 'dates' : 'ticked months'}`}
              </div>
            </div>
            {periodMode === 'months' && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {[
                  { label: 'This month', onClick: () => pickRecentMonths(1) },
                  { label: 'Last 3 months', onClick: () => pickRecentMonths(3) },
                  { label: 'Clear', onClick: () => setSelectedMonths([]) }
                ].map(({ label, onClick }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={onClick}
                    style={{ padding: '6px 12px', backgroundColor: '#EEEBE1', color: '#36433B', border: '1px solid #D2CDBF', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {periodMode === 'months' ? (
            <>
              {/* Year + month checkboxes */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '14px 0 10px' }}>
                <button
                  type="button"
                  aria-label="Previous year"
                  onClick={() => setPeriodYear(y => y - 1)}
                  style={{ padding: '4px 10px', backgroundColor: 'white', border: '1px solid #D2CDBF', borderRadius: '6px', cursor: 'pointer', color: '#36433B' }}
                >
                  ‹
                </button>
                <span style={{ fontSize: '15px', fontWeight: '700', color: '#36433B', minWidth: '44px', textAlign: 'center' }}>{periodYear}</span>
                <button
                  type="button"
                  aria-label="Next year"
                  onClick={() => setPeriodYear(y => y + 1)}
                  style={{ padding: '4px 10px', backgroundColor: 'white', border: '1px solid #D2CDBF', borderRadius: '6px', cursor: 'pointer', color: '#36433B' }}
                >
                  ›
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(6, 1fr)', gap: '8px' }}>
                {MONTH_LABELS.map((label, i) => {
                  const key = monthKey(periodYear, i + 1);
                  const checked = selectedMonths.includes(key);
                  const isCurrent = key === monthKey(today.getFullYear(), today.getMonth() + 1);
                  return (
                    <label
                      key={key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 10px',
                        borderRadius: '8px',
                        border: `1px solid ${checked ? '#1A3E2A' : '#D2CDBF'}`,
                        backgroundColor: checked ? '#E8EFE9' : '#FBFAF6',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: checked ? '600' : '500',
                        color: '#1F2622'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleMonth(key)}
                        style={{ accentColor: '#1A3E2A', width: '16px', height: '16px', margin: 0 }}
                      />
                      {label}
                      {isCurrent && <span style={{ marginLeft: 'auto', fontSize: '10px', color: '#A9613C', fontWeight: '700' }}>NOW</span>}
                    </label>
                  );
                })}
              </div>
            </>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 220px))', gap: '12px', marginTop: '14px' }}>
              {[['From', rangeFrom, setRangeFrom], ['To', rangeTo, setRangeTo]].map(([label, value, setValue]) => (
                <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#36433B' }}>{label}</label>
                  <input
                    type="date"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    style={{ padding: '8px 12px', border: '1px solid #D2CDBF', borderRadius: '6px', fontSize: '14px', outline: 'none' }}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Selection summary + load */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginTop: '14px' }}>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center', minWidth: 0 }}>
              {periodMode === 'range' ? (
                <span style={{ fontSize: '13px', color: '#5F6B61' }}>
                  {rangeFrom && rangeTo ? `${dayLabel(rangeFrom)} to ${dayLabel(rangeTo)}` : 'Choose a start and an end date'}
                </span>
              ) : selectedMonths.length === 0 ? (
                <span style={{ fontSize: '13px', color: '#5F6B61' }}>No months ticked</span>
              ) : (
                [...selectedMonths].sort().map(key => (
                  <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: '12px', backgroundColor: '#F6EBD6', color: '#5C3E18', fontSize: '12px', fontWeight: '600' }}>
                    {monthLabel(key)}
                    <button
                      type="button"
                      aria-label={`Remove ${monthLabel(key)}`}
                      onClick={() => toggleMonth(key)}
                      style={{ border: 'none', background: 'none', color: '#5C3E18', cursor: 'pointer', padding: 0, fontSize: '14px', lineHeight: 1 }}
                    >
                      ×
                    </button>
                  </span>
                ))
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', width: isMobile ? '100%' : 'auto' }}>
              <span style={{ fontSize: '12px', color: selectionChanged ? '#A9613C' : '#5F6B61', fontWeight: selectionChanged ? '600' : '400' }}>
                {selectionChanged
                  ? 'Selection changed: press Load report'
                  : appliedPeriod
                    ? `Showing ${periodLabel(appliedPeriod)}`
                    : 'Nothing loaded yet'}
              </span>
              <button
                type="button"
                onClick={loadSelectedPeriod}
                disabled={loading || !canLoadPeriod}
                style={{
                  padding: '10px 20px',
                  backgroundColor: loading || !canLoadPeriod ? '#A5A89B' : '#1A3E2A',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: loading || !canLoadPeriod ? 'not-allowed' : 'pointer',
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                {loading ? 'Loading...' : `Load report${periodMode === 'months' && selectedMonths.length > 1 ? ` (${selectedMonths.length} months)` : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Actions */}
      <div style={{
        backgroundColor: 'white',
        padding: isMobile ? '12px' : '20px',
        borderRadius: '12px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'flex-end',
          gap: '16px',
          flexWrap: 'wrap',
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          {/* Filters Dropdown */}
          <div style={{ flex: 1, width: '100%' }}>
            <button
              onClick={() => setShowFiltersDropdown(prev => !prev)}
              style={{
                width: isMobile ? '100%' : 'fit-content',
                padding: '10px 14px',
                backgroundColor: '#EEEBE1',
                border: '1px solid #D2CDBF',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                color: '#36433B',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: isMobile ? 'space-between' : 'center',
                gap: '8px'
              }}
            >
              <span>Filters</span>
              <span>{showFiltersDropdown ? '▲' : '▼'}</span>
            </button>

            {showFiltersDropdown && (
              <div style={{
                marginTop: '12px',
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '12px',
                padding: isMobile ? '10px' : '12px',
                border: '1px solid #E3DFD3',
                borderRadius: '10px',
                backgroundColor: '#F5F3EB'
              }}>
                {renderFilters()}
              </div>
            )}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap', width: isMobile ? '100%' : 'auto' }}>
            {activeTab === 'master-booking' && (
              <>
                <button
                  onClick={clearAllFilters}
                  disabled={loading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: loading ? '#A5A89B' : '#A13F2F',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: isMobile ? '100%' : 'auto',
                    justifyContent: 'center'
                  }}
                >
                  Clear Filters
                </button>
                
                {/* View Mode Toggle - 3-way button group */}
                <div style={{
                  display: 'flex',
                  borderRadius: '6px',
                  overflow: 'hidden',
                  border: '1px solid #D2CDBF',
                  width: isMobile ? '100%' : 'auto'
                }}>
                  {[
                    { mode: 'expanded', label: 'Expanded', title: 'Separate columns for each entity' },
                    { mode: 'merged', label: 'Merged', title: 'Combines entities with comma separation + totals' },
                    { mode: 'accounts', label: 'Accounts', title: 'Financial view: rooms+beds combined, PB totals, payments/refunds' }
                  ].map(({ mode, label, title }) => (
                    <button
                      key={mode}
                      onClick={() => setViewMode(mode)}
                      disabled={loading || rawData.length === 0}
                      style={{
                        flex: isMobile ? 1 : 'none',
                        padding: '10px 16px',
                        backgroundColor: loading || rawData.length === 0 
                          ? '#A5A89B' 
                          : viewMode === mode 
                            ? (mode === 'accounts' ? '#163626' : mode === 'merged' ? '#A9613C' : '#1A3E2A')
                            : '#EEEBE1',
                        color: viewMode === mode ? 'white' : '#36433B',
                        border: 'none',
                        borderRight: mode !== 'accounts' ? '1px solid #D2CDBF' : 'none',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: loading || rawData.length === 0 ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        whiteSpace: 'nowrap'
                      }}
                      title={title}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </>
            )}
           
            {dynamicColumns.length > 0 && (
              <label
                title="Columns with no value in any loaded row (e.g. add-on services nobody used) are hidden; Export CSV still includes them"
                style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#36433B', padding: '10px 4px', cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                <input
                  type="checkbox"
                  checked={hideEmptyColumns}
                  onChange={(e) => setHideEmptyColumns(e.target.checked)}
                  style={{ accentColor: '#1A3E2A', margin: 0 }}
                />
                Hide empty columns{hideEmptyColumns && hiddenColumnCount > 0 ? ` (${hiddenColumnCount} hidden)` : ''}
              </label>
            )}
            <button
              onClick={fetchReport}
              disabled={loading || (isMonthScoped && !appliedPeriod)}
              style={{
                padding: '10px 20px',
                backgroundColor: loading ? '#A5A89B' : '#1A3E2A',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: isMobile ? '100%' : 'auto',
                justifyContent: 'center'
              }}
            >
              {loading ? '' : ''} {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* View Mode Indicator (for master-booking) */}
      {activeTab === 'master-booking' && rawData.length > 0 && (
        <div style={{
          backgroundColor: viewMode === 'accounts' ? '#F0F3EE' : viewMode === 'expanded' ? '#F0F3EE' : '#F8F0EA',
          padding: '12px 20px',
          borderRadius: '8px',
          marginBottom: '16px',
          border: viewMode === 'accounts' ? '1px solid #C4D4C8' : viewMode === 'expanded' ? '1px solid #C4D4C8' : '1px solid #D49E7F',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span style={{ fontSize: '18px' }}>{viewMode === 'accounts' ? '' : viewMode === 'expanded' ? '' : ''}</span>
          <div>
            <div style={{ 
              fontSize: '13px', 
              fontWeight: '600', 
              color: viewMode === 'accounts' ? '#0F2619' : viewMode === 'expanded' ? '#0F2619' : '#663923',
              marginBottom: '2px'
            }}>
              {viewMode === 'accounts' ? 'Accounts View Active' : viewMode === 'expanded' ? 'Expanded View Active' : 'Merged View Active'}
            </div>
            <div style={{ fontSize: '12px', color: '#5F6B61' }}>
              {viewMode === 'accounts'
                ? 'Financial/tax view: Rooms+Beds combined with price breakdowns totaled by position. Addons expanded individually. Payments & refunds listed with amount, date, method.'
                : viewMode === 'expanded' 
                  ? 'Multiple entities displayed in separate columns (e.g., Room 1, Room 2, Room 3...)' 
                  : 'Multiple entities merged with comma separation. Amount columns show both individual values and totals (e.g., "100, 200, 300" + Total: 600)'}
            </div>
          </div>
        </div>
      )}

      {/* Summary */}
      {renderSummary()}
      {/* Loading Progress Indicator */}
      {loading && loadingProgress.total > 1 && (
        <div style={{
          backgroundColor: '#F0F3EE',
          padding: '16px 24px',
          borderRadius: '8px',
          marginBottom: '24px',
          border: '1px solid #C4D4C8',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{ fontSize: '20px' }}><HourglassIcon size="1em" aria-hidden="true" /></div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#0F2619', marginBottom: '8px' }}>
              Fetching all records from backend...
            </div>
            <div style={{ fontSize: '12px', color: '#1A3E2A', marginBottom: '6px' }}>
              Page {loadingProgress.current} of {loadingProgress.total}
            </div>
            <div style={{
              width: '100%',
              height: '6px',
              backgroundColor: '#E1E9E2',
              borderRadius: '3px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${(loadingProgress.current / loadingProgress.total) * 100}%`,
                height: '100%',
                backgroundColor: '#1A3E2A',
                transition: 'width 0.3s ease',
                borderRadius: '3px'
              }} />
            </div>
          </div>
        </div>
      )}

      {/* Report Table */}
      <DynamicReportsTable
        key={activeTab}
        data={reportData}
        columns={[
          ...(viewMode === 'accounts' && activeTab === 'master-booking'
            ? [
                { key: 'userName', label: 'Guest Name', minWidth: '150px' },
                { key: 'checkInDate', label: 'Check-in', type: 'date', minWidth: '110px' },
                { key: 'checkOutDate', label: 'Check-out', type: 'date', minWidth: '110px' },
                { key: 'finalAmount', label: 'Booking Total', type: 'currency', align: 'right', minWidth: '120px' },
              ]
            : getColumnsForReport(activeTab)),
          ...visibleDynamicColumns
        ]}
        loading={loading}
        onExport={handleExport}
        title={reportTabs.find(t => t.id === activeTab)?.label}
        itemsPerPageOptions={[25, 50, 100, 250]}
        defaultItemsPerPage={50}
        emptyTitle={isMonthScoped && !appliedPeriod ? 'Choose the period to load' : undefined}
        emptyMessage={isMonthScoped && !appliedPeriod
          ? 'Tick months (or switch to Date range) in Period above and press Load report. Only that period is fetched.'
          : undefined}
        disableSorting={activeTab !== 'daily-summary'}
      />
    </div>
  );
};

export default NewReports;
